/// <reference lib="es2015" />
// ---------------------------------------------------------------------------
//
// Limitless CCXT Exchange adapter  (https://limitless.exchange)
//
// Hierarchy:  Group markets (events) → Child markets → YES/NO outcomes
//
// Each child market becomes one CCXT market with an outcomes list:
//   market.id:     slug
//   market.symbol: SLUG_SHORT
//   outcomes[i].symbol: SLUG_SHORT:YES  /  SLUG_SHORT:NO
//
// Sizes in the order book are in USDC micro-units (6 decimals) → ÷ 1_000_000.
//
// ---------------------------------------------------------------------------

import { sha256 } from '@noble/hashes/sha2.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import { keccak_256 as keccak } from '@noble/hashes/sha3.js';
import Exchange from '../abstract/prediction/limitless.js';
import type {
    int,
    Int, Str, Num, Dict,
    Strings,
    Market, PredictionOrderBook, OHLCV,
    Bool,
    Account, fetchEventsParams,
    PredictionEvent, PredictionTicker, PredictionTickers, PredictionOrder, PredictionTrade, PredictionPosition,
} from '../base/types.js';
import { ArgumentsRequired, BadRequest, InvalidAddress, InvalidOrder, OrderNotFound } from '../base/errors.js';
import { Precise } from '../base/Precise.js';
import { ecdsa } from '../base/functions.js';

// ---------------------------------------------------------------------------

/**
 * @class limitless
 * @augments Exchange
 */
export default class limitless extends Exchange {
    describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'limitless',
            'name': 'Limitless',
            'countries': [],
            'rateLimit': 200,
            'certified': false,
            'pro': false,
            'has': {
                'CORS': undefined,
                'spot': false,
                'margin': false,
                'swap': false,
                'future': false,
                'option': false,
                'approve': true,
                'cancelAllOrders': true,
                'cancelOrder': true,
                'cancelOrders': true,
                'createOrder': true,
                'fetchAccounts': true,
                'fetchBalance': false,
                'fetchClosedOrders': true,
                'fetchCurrencies': false,
                'fetchEvent': true,
                'fetchEvents': true,
                'fetchMarkets': true,
                'fetchMyTrades': true,
                'fetchOHLCV': true,
                'fetchOpenOrders': true,
                'fetchOrder': true,
                'fetchOrderBook': true,
                'fetchOrders': true,
                'fetchOrdersByIds': true,
                'fetchPositions': true,
                'fetchTicker': true,
                'fetchTickers': true,
                'fetchTrades': true,
                'prediction': true,
                'redeem': true,
            },
            'timeframes': {
                '1h': '1h',
                '6h': '6h',
                '1d': '1d',
                '1w': '1w',
                '1M': '1m',
            },
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/bb10c8b4-7b97-49f5-927c-057709dbaf7e',
                'api': {
                    'limitless': 'https://api.limitless.exchange',
                },
                'www': 'https://limitless.exchange',
                'doc': [ 'https://docs.limitless.exchange' ],
            },
            'api': {
                'limitless': {
                    'public': {
                        'get': {
                            'markets/active': 1,
                            'markets/active/{categoryId}': 1,
                            'categories': 1,
                            'markets/{addressOrSlug}': 1,
                            'markets/categories/count': 1,
                            'markets/active/slugs': 1,
                            'markets/search': 1,
                            'markets/{slug}/orderbook': 1,
                            'markets/{slug}/historical-price': 1,
                            'auth/signing-message': 1,
                            'markets/{addressOrSlug}/oracle-candles': 1,
                            'markets/{slug}/get-feed-events': 1,
                            'markets/{slug}/events': 1,
                            'markets/timeline': 1,
                            'markets/{slug}/timeline': 1,
                            'navigation': 1,
                            'market-pages/by-path': 1,
                            'market-pages/{id}/markets': 1,
                            'property-keys': 1,
                            'property-keys/{id}': 1,
                            'property-keys/{id}/options': 1,
                            'portfolio/{account}/traded-volume': 1,
                            'portfolio/{account}/positions': 1,
                            'portfolio/{account}/pnl-chart': 1,
                        },
                    },
                    'private': {
                        'get': {
                            'auth/api-keys': 1,
                            'profiles/partner-accounts': 1,
                            'markets/{slug}/user-orders': 1,
                            'portfolio/positions': 1,
                            'portfolio/trades': 1,
                            'markets/{slug}/locked-balance': 1,
                            'profiles/me': 1,
                            'profiles/{account}': 1,
                            'portfolio/pnl-chart': 1,
                            'portfolio/history': 1,
                            'portfolio/points': 1,
                            'portfolio/trading/allowance': 1,
                            'auth/api-tokens/capabilities': 1,
                            'auth/api-tokens': 1,
                            'profiles/partner-accounts/{profileId}/allowances': 1,
                        },
                        'post': {
                            'auth/logout': 1,
                            'auth/api-keys': 1,
                            'auth/login': 1,
                            'orders': 1,
                            'orders/cancel': 1,
                            'orders/cancel-batch': 1,
                            'orders/batch-cancel': 1,
                            'orders/status/batch': 1,
                            'portfolio/redeem': 1,
                            'portfolio/withdraw': 1,
                            'portfolio/withdrawal-addresses': 1,
                            'auth/api-tokens/derive': 1,
                            'profiles/partner-accounts': 1,
                            'profiles/partner-accounts/{profileId}/allowances/retry': 1,
                        },
                        'delete': {
                            'auth/api-keys': 1,
                            'orders/{order_id}': 1,
                            'orders/all/{slug}': 1,
                            'auth/api-tokens/{tokenId}': 1,
                            'portfolio/withdrawal-addresses/{address}': 1,
                        },
                    },
                },
            },
            'requiredCredentials': {
                'apiKey': true,   // Limitless API key
                'secret': true,
                'privateKey': true,   // embedded/trading wallet key — createOrder signs with it (env-var loading needs this true)
            },
            'fees': {
                'trading': {
                    'tierBased': false,
                    'percentage': true,
                    'maker': 0.02,
                    'taker': 0.02,
                },
            },
            'options': {
                'defaultFetchMarketsPages': 5,
                'marketsPageSize': 25,
                'usdcDecimals': 6,  // Limitless sizes are 6-decimal USDC
                'warnOnCancelAllOrdersWithOutcome': true, // cancelAllOrders with an outcome will cancel all orders for the entire slug (both YES and NO outcomes), so we warn by default to prevent mistakes. Set this option to false to suppress the warning.
                'zeroAddress': '0x0000000000000000000000000000000000000000',
                'chainId': 8453,  // Base
                'rpcUrl': 'https://mainnet.base.org',  // Base RPC used by approve() for the on-chain allowance tx
                'collateralAddress': '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',  // USDC on Base (default approve token)
                'exchangeAddress': '0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5',  // Limitless CTF exchange (default approve spender)
                'createMarketBuyOrderRequiresPrice': true,
            },
            'exceptions': {
                'exact': {},
                'broad': {
                    'Order not found': OrderNotFound,
                    'Orders not found': OrderNotFound,
                    'already canceled': OrderNotFound,
                    'must be a UUIDv4': BadRequest,
                    'Invalid signature': InvalidOrder,
                },
            },
        });
    }

    /**
     * @method
     * @name limitless#fetchMarkets
     * @description fetches all active limitless markets paginated and returns one CCXT market per child market, each containing a list of outcome objects (YES/NO)
     * @see https://docs.limitless.exchange/api-reference/markets/get-active-markets
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.query] a single search query string to filter markets by
     * @param {string[]} [params.queries] multiple search query strings (alternative to query)
     * @param {int} [params.limit] max number of markets to collect (defaults to options.fetchMarketsLimit, 1000); caps the pages fetched
     * @returns {object[]} an array of objects representing market data
     */
    async fetchMarkets (params = {}): Promise<Market[]> {
        const queries = this.parseSearchQueries (params) as any[];
        const rest = this.omit (params, [ 'query', 'queries', 'limit' ]);
        // scope the listing: without a search query loadMarkets would otherwise page through
        // every active limitless market. Cap the total number of markets collected.
        const maxMarkets = this.safeInteger (params, 'limit', this.safeInteger (this.options, 'fetchMarketsLimit', 1000));
        let allRaw: any[] = [];
        const queriesLength = queries.length;
        if (queries && queriesLength > 0) {
            const requestedLimit = this.safeInteger (params, 'limit', 50);
            // the search endpoint rejects limit > 50 - cap the per-query request and let
            // maxMarkets bound the overall collection
            const limit = Math.min (requestedLimit, 50);
            const searchRest = this.omit (rest, [ 'limit' ]);
            const seen: Dict = {};
            for (let i = 0; i < queries.length; i++) {
                const q = queries[i];
                const response = await this.limitlessPublicGetMarketsSearch (this.extend ({ 'query': q, 'limit': limit }, searchRest));
                const found = this.safeList (response, 'markets', []) as any[];
                for (let j = 0; j < found.length; j++) {
                    const raw = found[j];
                    const slug = this.safeString (raw, 'slug');
                    if (slug && !(slug in seen)) {
                        seen[slug] = true;
                        allRaw.push (raw);
                    }
                }
            }
        } else {
            let page = 1;
            const pageSize = this.safeInteger (this.options, 'marketsPageSize', 25);
            const request: Dict = {
                'page': page,
                'limit': pageSize,
            };
            const firstPageResponse = await this.limitlessPublicGetMarketsActive (this.extend (request, rest));
            const totalMarketsCount = this.safeInteger (firstPageResponse, 'totalMarketsCount');
            const firstData = this.safeList (firstPageResponse, 'data', []);
            allRaw = this.arrayConcat (allRaw, firstData);
            const promises = [];
            const cappedPages = Math.ceil (maxMarkets / pageSize);
            const knownTotal = (totalMarketsCount !== undefined) ? totalMarketsCount : 0;
            const allPages = Math.ceil (knownTotal / pageSize);
            const totalPages = Math.min (allPages, cappedPages);
            for (let i = 2; i <= totalPages; i++) {
                page = i;
                request['page'] = page;
                promises.push (this.limitlessPublicGetMarketsActive (this.extend (request, rest)));
            }
            const responses = await Promise.all (promises);
            const length = responses.length;
            for (let j = 0; j < length; j++) {
                const response = this.safeDict (responses, j);
                const data = this.safeList (response, 'data', []);
                allRaw = this.arrayConcat (allRaw, data);
            }
            const lastPageResponse = this.safeDict (responses, length - 1);
            const lastPageData = this.safeList (lastPageResponse, 'data', []);
            const lastPageLength = lastPageData.length;
            const allRawLength = allRaw.length;
            if (lastPageLength >= pageSize && allRawLength < maxMarkets) {
                while (true) {
                    page = this.sum (page, 1);
                    request['page'] = page;
                    const response = await this.limitlessPublicGetMarketsActive (this.extend (request, rest));
                    const rawPageMarkets = this.safeList (response, 'data', response as any);
                    const page_markets = (rawPageMarkets !== undefined) ? rawPageMarkets : [];
                    const pageMarketsLength = page_markets.length;
                    if (!page_markets || pageMarketsLength === 0) {
                        break;
                    }
                    for (let i = 0; i < page_markets.length; i++) {
                        const raw = page_markets[i];
                        allRaw.push (raw);
                    }
                    const allRawCount = allRaw.length;
                    if (pageMarketsLength < pageSize || allRawCount >= maxMarkets) {
                        break;
                    }
                }
            }
        }
        const markets: Market[] = [];
        const eventGroups: Dict = {};
        // group rows carry their tradeable children in a nested `markets` list — expand them
        // into regular rows before parsing (a group row itself has no tokens)
        const expandedRaw = this.expandGroupRows (allRaw);
        for (let i = 0; i < expandedRaw.length; i++) {
            const raw = expandedRaw[i];
            const groupId = this.safeStringN (raw, [ 'groupSlug', 'groupId' ], this.safeString (raw, 'slug'));
            const eventKey = groupId ? this.shortenSlug (groupId) : undefined;
            const m = this.parseMarket (raw);
            markets.push (m);
            if (eventKey) {
                if (!(eventKey in eventGroups)) {
                    eventGroups[eventKey] = { 'groupId': groupId, 'title': this.safeString2 (raw, 'groupTitle', 'title', groupId), 'raw': raw, 'markets': [] };
                }
                const eventGroup = eventGroups[eventKey] as Dict;
                // push through a local and write the slice back — the go transpiler's
                // AppendToArray reassigns only a local copy of a map-stored array, so a
                // direct push on eventGroup['markets'] loses the element in go
                const groupMarkets = eventGroup['markets'];
                groupMarkets.push (m);
                eventGroup['markets'] = groupMarkets;
            }
        }
        const eventsDict: Dict = {};
        const eventKeys = Object.keys (eventGroups);
        for (let i = 0; i < eventKeys.length; i++) {
            const eventKey = eventKeys[i];
            const g = eventGroups[eventKey] as Dict;
            eventsDict[eventKey] = this.parseEvent (g);
        }
        this.events = eventsDict;
        const marketsLength = markets.length;
        if (marketsLength > maxMarkets) {
            return this.arraySlice (markets, 0, maxMarkets);
        }
        return markets;
    }

    parseMarket (raw: Dict): Market {
        //
        // {
        //   "id":"36814",
        //   "automationType":"manual",
        //   "conditionId":"0x11287d02d8067ff3d3d8bd21b212ebcfdc20b638f7f6440e4115f649e6b57015",
        //   "negRiskRequestId":null,
        //   "description":"<p>This market will resolve to “Yes” if Donald Trump resigns or is removed as President or otherwise ceases to be the President of the United States for any period of time by December 31, 2026, 11:59 PM ET. Otherwise, this market will resolve to “No”.</p><p>An announcement of Donald Trump's resignation/removal before this market's end date will immediately resolve this market to \\""Yes\\"", regardless of when the announced resignation/removal goes into effect.</p><p>Only permanent removal from office will qualify. Temporary removal (e.g. temporary invocation of the 25th Amendment under Section 3 or a Section 4 invocation not sustained by both Houses of Congress) or impeachment without removal will not count.</p><p>A sustained invocation of the Twenty-Fifth Amendment, Section 4 (i.e., if both Houses of Congress, by two-thirds vote, uphold the Vice President and Cabinet’s determination of presidential inability) will qualify for a \\""Yes\\"" resolution.</p><p>The resolution source for this market will be a consensus of credible reporting.</p>",
        //   "collateralToken":{
        //       "address":"0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        //       "decimals":"6",
        //       "symbol":"USDC"
        //   },
        //   "title":"💎 Trump out as President before 2027?",
        //   "proxyTitle":null,
        //   "expirationDate":"Jan 1, 2027",
        //   "expirationTimestamp":"1798779540000",
        //   "createdAt":"2026-01-20T18:17:48.298Z",
        //   "updatedAt":"2026-02-24T17:00:11.833Z",
        //   "categories":[
        //       "Politics"
        //   ],
        //   "status":"FUNDED",
        //   "expired":false,
        //   "hidden":false,
        //   "creator":{
        //       "name":"Limitless",
        //       "imageURI":"https://limitless.exchange/assets/images/logo.svg",
        //       "link":"https://x.com/trylimitless"
        //   },
        //   "tags":[
        //       "Limitless"
        //   ],
        //   "volume":"290091252",
        //   "volumeFormatted":"290.091252",
        //   "tokens":{
        //       "yes":"56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //       "no":"32572248812801208874557774576516861470423415416073401354576860825663488568217"
        //   },
        //   "prices":[
        //       0.164,
        //       0.836
        //   ],
        //   "isOther":false,
        //   "isRewardable":true,
        //   "slug":"trump-out-as-president-before-2027-1768933068297",
        //   "tradeType":"clob",
        //   "venue":{
        //       "exchange":"0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //       "adapter":null
        //   },
        //   "marketType":"single",
        //   "priorityIndex":"0",
        //   "winningOutcomeIndex":null,
        //   "metadata":{
        //       "fee":true,
        //       "isBannered":false,
        //       "isPolyArbitrage":true
        //   },
        //   "trends":{
        //       "hourly":{
        //           "value":"3",
        //           "rank":"395"
        //       }
        //   },
        //   "settings":{
        //       "minSize":"100000000",
        //       "maxSpread":"0.035",
        //       "dailyReward":"5",
        //       "rewardsEpoch":"0.003472222222222222",
        //       "c":"3",
        //       "rebateRate":"0"
        //   },
        //   "imageUrl":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //   "logo":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png"
        // }
        //
        const slug = this.safeString (raw, 'slug');
        const address = this.safeString (raw, 'address', slug);
        // groupSlug is stamped by expandGroupRows on children of a group row — prefer it over
        // the child's own numeric groupId so symbols/handles derive from the readable slug
        const groupId = this.safeStringN (raw, [ 'groupSlug', 'groupId' ], slug);
        // CTF condition id — needed to redeem a resolved winning position
        const conditionId = this.safeString (raw, 'conditionId');
        const tokens = this.safeValue (raw, 'tokens', {});
        // the listing exposes `expired` + `status` (FUNDED/RESOLVED/…), not an `active` flag; a
        // market is tradeable only while it is FUNDED and not yet expired
        const isExpired = this.safeBool (raw, 'expired', false);
        const marketStatus = this.safeString (raw, 'status');
        const active = !isExpired && (marketStatus === 'FUNDED');
        // expiry is a ms timestamp string (`expirationTimestamp`); `deadline`/`expiresAt` do not exist
        const expiryTimestamp = this.safeInteger (raw, 'expirationTimestamp');
        // limitless reports lifetime volume (human-readable in `volumeFormatted`), not a 24h figure
        const volume24h = this.safeNumber (raw, 'volumeFormatted');
        // resolution: winningOutcomeIndex is null until the market resolves, then the winning outcome index
        const winningOutcomeIndex = this.safeInteger (raw, 'winningOutcomeIndex');
        const marketResolved = (winningOutcomeIndex !== undefined);
        let resolvedOutcome = undefined;
        const marketSymbol = this.slugToMarketSymbol (groupId, slug);
        // amount precision comes from the collateral token decimals (USDC, 6); limitless does not
        // expose a price tick, so 0.001 is the platform convention
        const collateralToken = this.safeDict (raw, 'collateralToken', {});
        const collateralDecimals = this.safeInteger (collateralToken, 'decimals', this.safeInteger (this.options, 'usdcDecimals', 6));
        const precision = {
            'amount': this.parseNumber (this.parsePrecision (this.numberToString (collateralDecimals))),
            'price': 0.001,
        };
        const outcomes: any[] = [];
        const tokenEntries = Object.keys (tokens);
        for (let i = 0; i < tokenEntries.length; i++) {
            const outcomeLabel = tokenEntries[i];
            const tokenData = tokens[outcomeLabel];
            const tokenId = tokenData;
            const outcomeHandle = this.slugToOutcomeSymbol (groupId, slug, outcomeLabel);
            // winningOutcomeIndex indexes the API's canonical outcome order (yes=0, no=1 for
            // limitless's binary yes/no markets). Object.keys iteration order is NOT stable across
            // languages (Go randomizes map iteration), so map the leg to its canonical index by
            // label rather than by loop position — otherwise Go/Java flag the wrong winner
            const labelLower = outcomeLabel.toLowerCase ();
            let legIndex = i;
            if (labelLower === 'yes') {
                legIndex = 0;
            } else if (labelLower === 'no') {
                legIndex = 1;
            }
            let winnerRaw = undefined;
            let settleFractionRaw = undefined;
            if (marketResolved) {
                winnerRaw = (legIndex === winningOutcomeIndex);
                settleFractionRaw = winnerRaw ? 1 : 0;
                if (winnerRaw) {
                    resolvedOutcome = outcomeHandle;
                }
            }
            // effectively-final copies for the object literal below (Java cannot capture a
            // reassigned local into the anonymous inner class it emits for a map literal)
            const winner = winnerRaw;
            const settleFraction = settleFractionRaw;
            outcomes.push ({
                'outcome': outcomeHandle,
                'outcomeId': tokenId,
                'market': marketSymbol,
                'label': outcomeLabel,
                'active': active,
                'winner': winner,
                'settleFraction': settleFraction,
                'precision': precision,
                'info': {
                    'slug': slug,
                    'address': address,
                    'conditionId': conditionId,
                    'outcomeLabel': outcomeLabel,
                    'tokenId': tokenId,
                    'volume24h': volume24h,
                },
            });
        }
        const outcomesLength = outcomes.length;
        // effectively-final copy for the market object literal below (reassigned in the loop)
        const marketResolvedOutcome = resolvedOutcome;
        return {
            'id': slug,
            'market': marketSymbol,
            'marketType': (outcomesLength > 2) ? 'categorical' : 'binary',
            'executionModel': 'clob',
            'collateral': 'USDC',
            'base': slug,
            'quote': 'USDC',
            'settle': undefined,
            'baseId': slug,
            'quoteId': 'USDC',
            'settleId': undefined,
            'type': 'prediction',
            'spot': false,
            'margin': false,
            'swap': false,
            'future': false,
            'option': false,
            'prediction': true,
            'active': active,
            'resolved': marketResolved,
            'resolvedOutcome': marketResolvedOutcome,
            'contract': false,
            'linear': undefined,
            'inverse': undefined,
            'contractSize': undefined,
            'expiry': expiryTimestamp,
            'expiryDatetime': this.iso8601 (expiryTimestamp),
            'strike': undefined,
            'optionType': undefined,
            'taker': 0.02,
            'maker': 0.02,
            'percentage': true,
            'tierBased': false,
            'feeSide': 'get',
            'precision': precision,
            'limits': {
                'leverage': { 'min': 1, 'max': 1 },
                'amount': { 'min': 0, 'max': undefined },
                'price': { 'min': 0.001, 'max': 0.999 },
                'cost': { 'min': undefined, 'max': undefined },
            },
            'outcomes': outcomes,
            'info': this.extend (raw, {
                'slug': slug,
                'address': address,
                'volume24h': volume24h,
            }),
            'created': undefined,
        } as unknown as Market;
    }

    /**
     * @method
     * @name limitless#fetchEvent
     * @description fetches a single prediction-market event by its market slug or address
     * @see https://docs.limitless.exchange/api-reference/markets/get-market
     * @param {string} id the market slug or address
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction event structure](https://docs.ccxt.com/#/?id=prediction-event-structure)
     */
    async fetchEvent (id: string, params = {}): Promise<PredictionEvent> {
        const request: Dict = { 'addressOrSlug': id };
        const response = await this.limitlessPublicGetMarketsAddressOrSlug (this.extend (request, params));
        // a group response carries its tradeable children in `markets` (each a full market row
        // with tokens) — expandGroupRows unwraps them; a single market has no nested markets
        // and wraps as its own one-market event, which parseEvent's loop then parses
        const rows = this.expandGroupRows ([ response ]);
        const wrapped = this.extend (response, { 'markets': rows });
        const event: any = this.parseEvent (wrapped);
        this.indexEventOutcomes (event);
        return event;
    }

    /**
     * @ignore
     * @method
     * @name limitless#expandGroupRows
     * @description flattens listing rows — a 'group' row carries no tradeable tokens itself and
     * its children (each a full market row with tokens) appear nowhere else in the listing, so
     * each group row is replaced by its nested markets, tagged with the group's slug and title
     * (the child's own groupId is an opaque numeric venue id) so they regroup under one readable event
     * @param {object[]} rawRows raw listing rows, single-market and group rows mixed
     * @returns {object[]} raw single-market rows only
     */
    expandGroupRows (rawRows: any[]): any[] {
        const result = [];
        for (let i = 0; i < rawRows.length; i++) {
            const raw = rawRows[i];
            const rowType = this.safeString (raw, 'marketType');
            const nestedMarkets = this.safeList (raw, 'markets');
            if ((rowType === 'group') && (nestedMarkets !== undefined)) {
                const groupSlug = this.safeString (raw, 'slug');
                const groupTitle = this.safeString (raw, 'title', groupSlug);
                const nestedMarketsLength = nestedMarkets.length;
                for (let j = 0; j < nestedMarketsLength; j++) {
                    // extend copies — the raw child stays untouched
                    const tagged = this.extend (nestedMarkets[j], { 'groupSlug': groupSlug, 'groupTitle': groupTitle });
                    result.push (tagged);
                }
            } else {
                result.push (raw);
            }
        }
        return result;
    }

    parseEvent (event: Dict): any {
        // {
        //    "groupId":"trump-out-as-president-before-2027-1768933068297",
        //    "title":"💎 Trump out as President before 2027?",
        //    "raw":{
        //       "id":"36814",
        //       "automationType":"manual",
        //       "conditionId":"0x11287d02d8067ff3d3d8bd21b212ebcfdc20b638f7f6440e4115f649e6b57015",
        //       "negRiskRequestId":null,
        //       "description":"<p>This market will resolve to “Yes” if Donald Trump resigns or is removed as President or otherwise ceases to be the President of the United States for any period of time by December 31, 2026, 11:59 PM ET. Otherwise, this market will resolve to “No”.</p><p>An announcement of Donald Trump's resignation/removal before this market's end date will immediately resolve this market to \\""Yes\\"", regardless of when the announced resignation/removal goes into effect.</p><p>Only permanent removal from office will qualify. Temporary removal (e.g. temporary invocation of the 25th Amendment under Section 3 or a Section 4 invocation not sustained by both Houses of Congress) or impeachment without removal will not count.</p><p>A sustained invocation of the Twenty-Fifth Amendment, Section 4 (i.e., if both Houses of Congress, by two-thirds vote, uphold the Vice President and Cabinet’s determination of presidential inability) will qualify for a \\""Yes\\"" resolution.</p><p>The resolution source for this market will be a consensus of credible reporting.</p>",
        //       "collateralToken":{
        //          "address":"0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        //          "decimals":"6",
        //          "symbol":"USDC"
        //       },
        //       "title":"💎 Trump out as President before 2027?",
        //       "proxyTitle":null,
        //       "expirationDate":"Jan 1, 2027",
        //       "expirationTimestamp":"1798779540000",
        //       "createdAt":"2026-01-20T18:17:48.298Z",
        //       "updatedAt":"2026-02-24T17:00:11.833Z",
        //       "categories":[
        //          "Politics"
        //       ],
        //       "status":"FUNDED",
        //       "expired":false,
        //       "hidden":false,
        //       "creator":{
        //          "name":"Limitless",
        //          "imageURI":"https://limitless.exchange/assets/images/logo.svg",
        //          "link":"https://x.com/trylimitless"
        //       },
        //       "tags":[
        //          "Limitless"
        //       ],
        //       "volume":"290091252",
        //       "volumeFormatted":"290.091252",
        //       "tokens":{
        //          "yes":"56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //          "no":"32572248812801208874557774576516861470423415416073401354576860825663488568217"
        //       },
        //       "prices":[
        //          0.164,
        //          0.836
        //       ],
        //       "isOther":false,
        //       "isRewardable":true,
        //       "slug":"trump-out-as-president-before-2027-1768933068297",
        //       "tradeType":"clob",
        //       "venue":{
        //          "exchange":"0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //          "adapter":null
        //       },
        //       "marketType":"single",
        //       "priorityIndex":"0",
        //       "winningOutcomeIndex":null,
        //       "metadata":{
        //          "fee":true,
        //          "isBannered":false,
        //          "isPolyArbitrage":true
        //       },
        //       "trends":{
        //          "hourly":{
        //             "value":"3",
        //             "rank":"395"
        //          }
        //       },
        //       "settings":{
        //          "minSize":"100000000",
        //          "maxSpread":"0.035",
        //          "dailyReward":"5",
        //          "rewardsEpoch":"0.003472222222222222",
        //          "c":"3",
        //          "rebateRate":"0"
        //       },
        //       "imageUrl":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //       "logo":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png"
        //    },
        //    "markets":[
        //       {
        //          "id":"trump-out-as-president-before-2027-1768933068297",
        //          "symbol":"TRUMP_OUT_PRESIDENT_2027_1768933068297",
        //          "base":"trump-out-as-president-before-2027-1768933068297",
        //          "quote":"USDC",
        //          "baseId":"trump-out-as-president-before-2027-1768933068297",
        //          "quoteId":"USDC",
        //          "type":"prediction",
        //          "spot":false,
        //          "margin":false,
        //          "swap":false,
        //          "future":false,
        //          "option":false,
        //          "prediction":true,
        //          "active":true,
        //          "contract":false,
        //          "taker":0.02,
        //          "maker":0.02,
        //          "percentage":true,
        //          "tierBased":false,
        //          "feeSide":"get",
        //          "precision":{
        //             "amount":0.000001,
        //             "price":0.001
        //          },
        //          "limits":{
        //             "leverage":{
        //                "min":1,
        //                "max":1
        //             },
        //             "amount":{
        //                "min":0
        //             },
        //             "price":{
        //                "min":0.001,
        //                "max":0.999
        //             },
        //             "cost":{
        //             }
        //          },
        //          "outcomes":[
        //             {
        //                "id":"trump-out-as-president-before-2027-1768933068297/yes",
        //                "symbol":"TRUMP_OUT_PRESIDENT_2027_1768933068297:YES",
        //                "marketSymbol":"TRUMP_OUT_PRESIDENT_2027_1768933068297",
        //                "label":"yes",
        //                "active":true,
        //                "info":{
        //                   "slug":"trump-out-as-president-before-2027-1768933068297",
        //                   "address":"trump-out-as-president-before-2027-1768933068297",
        //                   "outcomeLabel":"yes",
        //                   "tokenId":"trump-out-as-president-before-2027-1768933068297/yes"
        //                }
        //             },
        //             {
        //                "id":"trump-out-as-president-before-2027-1768933068297/no",
        //                "symbol":"TRUMP_OUT_PRESIDENT_2027_1768933068297:NO",
        //                "marketSymbol":"TRUMP_OUT_PRESIDENT_2027_1768933068297",
        //                "label":"no",
        //                "active":true,
        //                "info":{
        //                   "slug":"trump-out-as-president-before-2027-1768933068297",
        //                   "address":"trump-out-as-president-before-2027-1768933068297",
        //                   "outcomeLabel":"no",
        //                   "tokenId":"trump-out-as-president-before-2027-1768933068297/no"
        //                }
        //             }
        //          ],
        //          "info":{
        //             "id":"36814",
        //             "automationType":"manual",
        //             "conditionId":"0x11287d02d8067ff3d3d8bd21b212ebcfdc20b638f7f6440e4115f649e6b57015",
        //             "negRiskRequestId":null,
        //             "description":"<p>This market will resolve to “Yes” if Donald Trump resigns or is removed as President or otherwise ceases to be the President of the United States for any period of time by December 31, 2026, 11:59 PM ET. Otherwise, this market will resolve to “No”.</p><p>An announcement of Donald Trump's resignation/removal before this market's end date will immediately resolve this market to \\""Yes\\"", regardless of when the announced resignation/removal goes into effect.</p><p>Only permanent removal from office will qualify. Temporary removal (e.g. temporary invocation of the 25th Amendment under Section 3 or a Section 4 invocation not sustained by both Houses of Congress) or impeachment without removal will not count.</p><p>A sustained invocation of the Twenty-Fifth Amendment, Section 4 (i.e., if both Houses of Congress, by two-thirds vote, uphold the Vice President and Cabinet’s determination of presidential inability) will qualify for a \\""Yes\\"" resolution.</p><p>The resolution source for this market will be a consensus of credible reporting.</p>",
        //             "collateralToken":{
        //                "address":"0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        //                "decimals":"6",
        //                "symbol":"USDC"
        //             },
        //             "title":"💎 Trump out as President before 2027?",
        //             "proxyTitle":null,
        //             "expirationDate":"Jan 1, 2027",
        //             "expirationTimestamp":"1798779540000",
        //             "createdAt":"2026-01-20T18:17:48.298Z",
        //             "updatedAt":"2026-02-24T17:00:11.833Z",
        //             "categories":[
        //                "Politics"
        //             ],
        //             "status":"FUNDED",
        //             "expired":false,
        //             "hidden":false,
        //             "creator":{
        //                "name":"Limitless",
        //                "imageURI":"https://limitless.exchange/assets/images/logo.svg",
        //                "link":"https://x.com/trylimitless"
        //             },
        //             "tags":[
        //                "Limitless"
        //             ],
        //             "volume":"290091252",
        //             "volumeFormatted":"290.091252",
        //             "tokens":{
        //                "yes":"56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //                "no":"32572248812801208874557774576516861470423415416073401354576860825663488568217"
        //             },
        //             "prices":[
        //                0.164,
        //                0.836
        //             ],
        //             "isOther":false,
        //             "isRewardable":true,
        //             "slug":"trump-out-as-president-before-2027-1768933068297",
        //             "tradeType":"clob",
        //             "venue":{
        //                "exchange":"0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //                "adapter":null
        //             },
        //             "marketType":"single",
        //             "priorityIndex":"0",
        //             "winningOutcomeIndex":null,
        //             "metadata":{
        //                "fee":true,
        //                "isBannered":false,
        //                "isPolyArbitrage":true
        //             },
        //             "trends":{
        //                "hourly":{
        //                   "value":"3",
        //                   "rank":"395"
        //                }
        //             },
        //             "settings":{
        //                "minSize":"100000000",
        //                "maxSpread":"0.035",
        //                "dailyReward":"5",
        //                "rewardsEpoch":"0.003472222222222222",
        //                "c":"3",
        //                "rebateRate":"0"
        //             },
        //             "imageUrl":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //             "logo":"https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //             "address":"trump-out-as-president-before-2027-1768933068297"
        //          }
        //       }
        //    ]
        // }
        const groupId = this.safeString (event, 'address', this.safeString (event, 'groupId', this.safeString (event, 'slug')));
        const endDate = this.safeString (event, 'deadline', this.safeString (event, 'expiresAt'));
        const title = this.safeString (event, 'title', groupId);
        const markets = [];
        const rawMarkets = this.safeList (event, 'markets', []);
        // aggregate 24h volume across the markets so sort by volume works
        let totalVolume = 0;
        for (let i = 0; i < rawMarkets.length; i++) {
            const rawMarket = rawMarkets[i];
            // an already-parsed ccxt market row carries the unified 'market' handle + outcomes
            // with 'symbol' kept as a legacy fallback — don't run it through parseMarket again
            const marketSymbol = this.safeString2 (rawMarket, 'market', 'symbol');
            const marketOutcomes = this.safeList (rawMarket, 'outcomes');
            if (marketSymbol !== undefined && marketOutcomes !== undefined) {
                markets.push (rawMarket);
            } else {
                markets.push (this.parseMarket (rawMarket));
            }
            const marketInfo = this.safeDict (rawMarket, 'info', rawMarket);
            // use volumeFormatted (human units) — the raw `volume` is 1e-6 fixed-point, which would
            // make the event volume 1,000,000x too big and useless for cross-venue ranking
            totalVolume = this.sum (totalVolume, this.safeNumber (marketInfo, 'volumeFormatted', 0));
        }
        return this.extend ({
            'id': groupId,
            'slug': groupId,
            'event': groupId ? this.shortenSlug (groupId) : undefined,
            'title': title,
            'description': this.safeString (event, 'description'),
            'markets': markets,
            'volume': totalVolume,
            'liquidity': this.safeNumber (event, 'liquidity'),
            'url': this.safeString (event, 'url'),
            'image': this.safeString (event, 'imageUrl', this.safeString (event, 'image')),
            'active': this.safeBool (event, 'active', true),
            'resolved': this.safeBool (event, 'resolved', false),
            'category': this.safeString (event, 'category'),
            'tags': this.safeList (event, 'tags'),
            'created': this.parse8601 (this.safeString (event, 'createdAt')),
            'createdDatetime': this.safeString (event, 'createdAt'),
            'end': endDate ? this.parse8601 (endDate) : undefined,
            'endDatetime': endDate,
            'lastUpdatedAt': this.parse8601 (this.safeString (event, 'updatedAt')),
            'resolutionSource': this.safeString (event, 'resolutionSource'),
            'info': event,
        }) as any;
    }

    /**
     * @method
     * @name limitless#fetchTicker
     * @description fetches the current price and best bid/ask for a single outcome token, combining the market detail and order book endpoints
     * @see https://docs.limitless.exchange/api-reference/markets/get-market
     * @see https://docs.limitless.exchange/api-reference/trading/orderbook
     * @param {string} outcome unified outcome like TRUMP_OUT_PRESIDENT_2027:YES or an outcome token id
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction ticker structure](https://docs.ccxt.com/#/?id=prediction-ticker-structure)
     */
    async fetchTicker (outcome: Str, params = {}): Promise<PredictionTicker> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const slug = this.safeString (outcomeObj['info'], 'slug');
        const request: Dict = {
            'addressOrSlug': slug,
        };
        const promises = [
            this.limitlessPublicGetMarketsAddressOrSlug (this.extend (request, params)),
            this.limitlessPublicGetMarketsSlugOrderbook ({ 'slug': slug }),
        ];
        const responses = await Promise.all (promises);
        const response = responses[0];
        //
        //     {
        //         "id": "36814",
        //         "automationType": "manual",
        //         "conditionId": "0x11287d02d8067ff3d3d8bd21b212ebcfdc20b638f7f6440e4115f649e6b57015",
        //         "negRiskRequestId": null,
        //         "description": "<p>This market will resolve to Yes if Donald Trump resigns or is removed as President or otherwise ceases to be the President of the United States for any period of time by December 31, 2026, 11:59 PM ET. Otherwise, this market will resolve to No.</p><p>An announcement of Donald Trump's resignation/removal before this market's end date will immediately resolve this market to Yes, regardless of when the announced resignation/removal goes into effect.</p><p>Only permanent removal from office will qualify. Temporary removal (e.g. temporary invocation of the 25th Amendment under Section 3 or a Section 4 invocation not sustained by both Houses of Congress) or impeachment without removal will not count.</p><p>A sustained invocation of the Twenty-Fifth Amendment, Section 4 (i.e., if both Houses of Congress, by two-thirds vote, uphold the Vice President and Cabinets determination of presidential inability) will qualify for a Yes resolution.</p><p>The resolution source for this market will be a consensus of credible reporting.</p>",
        //         "collateralToken": {
        //             "address": "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        //             "decimals": "6",
        //             "symbol": "USDC"
        //         },
        //         "title": "Trump out as President before 2027?",
        //         "proxyTitle": null,
        //         "expirationDate": "Jan 1, 2027",
        //         "expirationTimestamp": "1798779540000",
        //         "createdAt": "2026-01-20T18:17:48.298Z",
        //         "updatedAt": "2026-04-09T10:47:02.254Z",
        //         "categories": [ "Politics" ],
        //         "status": "FUNDED",
        //         "expired": false,
        //         "hidden": false,
        //         "creator": {
        //             "name": "Limitless",
        //             "imageURI": "https://limitless.exchange/assets/images/logo.svg",
        //             "link": "https://x.com/trylimitless"
        //         },
        //         "tags": [ "Limitless" ],
        //         "volume": "1032001807",
        //         "volumeFormatted": "1032.001807",
        //         "tokens": {
        //             "yes": "56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //             "no": "32572248812801208874557774576516861470423415416073401354576860825663488568217"
        //         },
        //         "prices": [ 0.155, 0.845 ],
        //         "tradePrices": {
        //             "buy": { "market": [Array], "limit": [Array] },
        //             "sell": { "market": [Array], "limit": [Array] }
        //         },
        //         "isOther": false,
        //         "isRewardable": true,
        //         "slug": "trump-out-as-president-before-2027-1768933068297",
        //         "tradeType": "clob",
        //         "venue": {
        //             "exchange": "0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //             "adapter": null
        //         },
        //         "marketType": "single",
        //         "priorityIndex": "0",
        //         "winningOutcomeIndex": null,
        //         "metadata": { "fee": true, "isBannered": false, "isPolyArbitrage": true },
        //         "settings": {
        //             "minSize": "100000000",
        //             "maxSpread": "0.035",
        //             "dailyReward": "5",
        //             "rewardsEpoch": "0.003472222222222222",
        //             "c": "3",
        //             "rebateRate": "0"
        //         },
        //         "imageUrl": "https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //         "logo": "https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png"
        //     }
        //
        const tickerInput: Dict = { 'market': response, 'book': responses[1] };
        return this.parsePredictionTicker (tickerInput, outcomeObj);
    }

    /**
     * @ignore
     * @method
     * @name limitless#parsePredictionTicker
     * @description parses a raw market object, or a composite market + book dict, into a unified ticker for the specified outcome token
     * @param {object} ticker a raw limitless market object or a dict with market and book entries
     * @param {object} [market] the outcome object the ticker belongs to
     * @returns {object} a [prediction ticker structure](https://docs.ccxt.com/#/?id=prediction-ticker-structure)
     */
    parsePredictionTicker (ticker: Dict, market: Market = undefined): PredictionTicker {
        //
        //     {
        //         "id": "36814",
        //         "automationType": "manual",
        //         "conditionId": "0x11287d02d8067ff3d3d8bd21b212ebcfdc20b638f7f6440e4115f649e6b57015",
        //         "negRiskRequestId": null,
        //         "description": "<p>This market will resolve to Yes if Donald Trump resigns or is removed as President or otherwise ceases to be the President of the United States for any period of time by December 31, 2026, 11:59 PM ET. Otherwise, this market will resolve to No.</p><p>An announcement of Donald Trump's resignation/removal before this market's end date will immediately resolve this market to Yes, regardless of when the announced resignation/removal goes into effect.</p><p>Only permanent removal from office will qualify. Temporary removal (e.g. temporary invocation of the 25th Amendment under Section 3 or a Section 4 invocation not sustained by both Houses of Congress) or impeachment without removal will not count.</p><p>A sustained invocation of the Twenty-Fifth Amendment, Section 4 (i.e., if both Houses of Congress, by two-thirds vote, uphold the Vice President and Cabinets determination of presidential inability) will qualify for a Yes resolution.</p><p>The resolution source for this market will be a consensus of credible reporting.</p>",
        //         "collateralToken": {
        //             "address": "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        //             "decimals": "6",
        //             "symbol": "USDC"
        //         },
        //         "title": "Trump out as President before 2027?",
        //         "proxyTitle": null,
        //         "expirationDate": "Jan 1, 2027",
        //         "expirationTimestamp": "1798779540000",
        //         "createdAt": "2026-01-20T18:17:48.298Z",
        //         "updatedAt": "2026-04-09T10:47:02.254Z",
        //         "categories": [ "Politics" ],
        //         "status": "FUNDED",
        //         "expired": false,
        //         "hidden": false,
        //         "creator": {
        //             "name": "Limitless",
        //             "imageURI": "https://limitless.exchange/assets/images/logo.svg",
        //             "link": "https://x.com/trylimitless"
        //         },
        //         "tags": [ "Limitless" ],
        //         "volume": "1032001807",
        //         "volumeFormatted": "1032.001807",
        //         "tokens": {
        //             "yes": "56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //             "no": "32572248812801208874557774576516861470423415416073401354576860825663488568217"
        //         },
        //         "prices": [ 0.155, 0.845 ],
        //         "tradePrices": {
        //             "buy": { "market": [Array], "limit": [Array] },
        //             "sell": { "market": [Array], "limit": [Array] }
        //         },
        //         "isOther": false,
        //         "isRewardable": true,
        //         "slug": "trump-out-as-president-before-2027-1768933068297",
        //         "tradeType": "clob",
        //         "venue": {
        //             "exchange": "0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //             "adapter": null
        //         },
        //         "marketType": "single",
        //         "priorityIndex": "0",
        //         "winningOutcomeIndex": null,
        //         "metadata": { "fee": true, "isBannered": false, "isPolyArbitrage": true },
        //         "settings": {
        //             "minSize": "100000000",
        //             "maxSpread": "0.035",
        //             "dailyReward": "5",
        //             "rewardsEpoch": "0.003472222222222222",
        //             "c": "3",
        //             "rebateRate": "0"
        //         },
        //         "imageUrl": "https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png",
        //         "logo": "https://cdn.limitless.exchange/markets-logo/36814/9daba01d-6bcd-4a2c-9187-f4264b7191da.png"
        //     }
        //
        // ticker is either a plain raw market object, or a composite dict { 'market': rawMarket, 'book': rawOrderbook }
        let raw = ticker;
        let book = undefined;
        if ('market' in ticker) {
            raw = this.safeDict (ticker, 'market', {});
            book = this.safeDict (ticker, 'book');
        }
        const rawLabel = (market !== undefined) ? this.safeString (market, 'label', this.safeString (market['info'], 'outcomeLabel', 'yes')) : 'yes';
        const isYes = rawLabel.toLowerCase () !== 'no';
        let bidStr = undefined;
        let askStr = undefined;
        let bidSizeStr = undefined;
        let askSizeStr = undefined;
        let lastStr = undefined;
        let midStr = undefined;
        if (book !== undefined) {
            // the book endpoint is quoted in the yes token, the no side mirrors at 1 - price
            const rawBids = this.safeList (book, 'bids', []) as any[];
            const rawAsks = this.safeList (book, 'asks', []) as any[];
            const rawBidsLength = rawBids.length;
            const rawAsksLength = rawAsks.length;
            const yesBestBid = (rawBidsLength > 0) ? rawBids[0] : undefined;
            const yesBestAsk = (rawAsksLength > 0) ? rawAsks[0] : undefined;
            const yesBidPrice = this.safeString (yesBestBid, 'price');
            const yesBidSize = this.safeString (yesBestBid, 'size');
            const yesAskPrice = this.safeString (yesBestAsk, 'price');
            const yesAskSize = this.safeString (yesBestAsk, 'size');
            const yesLast = this.safeString (book, 'lastTradePrice');
            const yesMid = this.safeString (book, 'midpoint');
            if (isYes) {
                bidStr = yesBidPrice;
                bidSizeStr = yesBidSize;
                askStr = yesAskPrice;
                askSizeStr = yesAskSize;
                lastStr = yesLast;
                midStr = yesMid;
            } else {
                if (yesAskPrice !== undefined) {
                    bidStr = Precise.stringSub ('1', yesAskPrice);
                }
                bidSizeStr = yesAskSize;
                if (yesBidPrice !== undefined) {
                    askStr = Precise.stringSub ('1', yesBidPrice);
                }
                askSizeStr = yesBidSize;
                if (yesLast !== undefined) {
                    lastStr = Precise.stringSub ('1', yesLast);
                }
                if (yesMid !== undefined) {
                    midStr = Precise.stringSub ('1', yesMid);
                }
            }
        }
        const prices = this.safeList (raw, 'prices', []) as any[];
        const pricesLength = prices.length;
        if ((lastStr === undefined) && (pricesLength > 0)) {
            lastStr = (isYes) ? this.safeString (prices, 0) : this.safeString (prices, 1);
        }
        // volume and book sizes are in USDC micro-units (6 decimals)
        const rawVolume = this.safeString (raw, 'volume');
        let volumeStr = undefined;
        if (rawVolume !== undefined) {
            volumeStr = Precise.stringDiv (rawVolume, '1000000');
        }
        if (bidSizeStr !== undefined) {
            bidSizeStr = Precise.stringDiv (bidSizeStr, '1000000');
        }
        if (askSizeStr !== undefined) {
            askSizeStr = Precise.stringDiv (askSizeStr, '1000000');
        }
        const now = this.milliseconds ();
        const outcomeSymbol = this.safeOutcomeSymbol (undefined, market);
        return this.safePredictionTicker ({
            'outcome': outcomeSymbol,
            'outcomeId': this.safeString (market, 'outcomeId'),
            'label': this.safeString (market, 'label'),
            'market': this.safeString (market, 'market'),
            'timestamp': now,
            'datetime': this.iso8601 (now),
            'high': undefined,
            'low': undefined,
            'bid': this.parseNumber (bidStr),
            'bidVolume': this.parseNumber (bidSizeStr),
            'ask': this.parseNumber (askStr),
            'askVolume': this.parseNumber (askSizeStr),
            'vwap': undefined,
            'open': undefined,
            'close': this.parseNumber (lastStr),
            'last': this.parseNumber (lastStr),
            'previousClose': undefined,
            'change': undefined,
            'percentage': undefined,
            'average': this.parseNumber (midStr),
            'baseVolume': undefined,
            'quoteVolume': this.parseNumber (volumeStr),
            'info': ticker,
        });
    }

    /**
     * @method
     * @name limitless#fetchTickers
     * @description fetches tickers for multiple outcome tokens, grouping requested outcomes by their parent market (two requests per market: detail + order book)
     * @see https://docs.limitless.exchange/api-reference/markets/get-market
     * @see https://docs.limitless.exchange/api-reference/trading/orderbook
     * @param {string[]} outcomes unified outcomes or outcome token ids — required: limitless has no endpoint returning all tickers at once, so an unscoped call is not supported
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a dictionary of [prediction ticker structures](https://docs.ccxt.com/#/?id=prediction-ticker-structure) indexed by outcome
     */
    async fetchTickers (outcomes: Strings = undefined, params = {}): Promise<PredictionTickers> {
        if (outcomes === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchTickers() requires an outcomes argument — the venue has no all-tickers endpoint; pass the outcome handles to fetch (discover them via fetchEvents ())');
        }
        const result: PredictionTickers = {};
        // resolve the uncached outcomes first, then group by parent market to fetch each
        // market and book only once
        await this.loadOutcomes (outcomes);
        const outcomesBySlug: Dict = {};
        const slugs: any[] = [];
        for (let i = 0; i < outcomes.length; i++) {
            const outcomeObj = this.outcome (outcomes[i]);
            const slug = this.safeString (outcomeObj['info'], 'slug');
            if (!(slug in outcomesBySlug)) {
                outcomesBySlug[slug] = [];
                slugs.push (slug);
            }
            // reassign after push, plain mutation through a local is lost in transpiled php (arrays are value types there)
            const grouped = outcomesBySlug[slug];
            grouped.push (outcomeObj);
            outcomesBySlug[slug] = grouped;
        }
        const promises: any[] = [];
        for (let i = 0; i < slugs.length; i++) {
            const slug = slugs[i];
            promises.push (this.limitlessPublicGetMarketsAddressOrSlug (this.extend ({ 'addressOrSlug': slug }, params)));
            promises.push (this.limitlessPublicGetMarketsSlugOrderbook ({ 'slug': slug }));
        }
        const responses = await Promise.all (promises);
        for (let i = 0; i < slugs.length; i++) {
            const slug = slugs[i];
            const detailIndex = i * 2;
            const detail = responses[detailIndex];
            const book = responses[this.sum (detailIndex, 1)];
            const tickerInput: Dict = { 'market': detail, 'book': book };
            const grouped = outcomesBySlug[slug] as any[];
            for (let j = 0; j < grouped.length; j++) {
                const ticker = this.parsePredictionTicker (tickerInput, grouped[j]);
                const symbolKey = this.safeString (ticker, 'outcome');
                if (symbolKey !== undefined) {
                    result[symbolKey] = ticker;
                }
            }
        }
        return result;
    }

    /**
     * @method
     * @name limitless#fetchTrades
     * @description fetches recent public trades for a single outcome token from the market events feed
     * @see https://docs.limitless.exchange/api-reference/trading/market-events
     * @param {string} outcome unified outcome like TRUMP_OUT_PRESIDENT_2027:YES or an outcome token id
     * @param {int} [since] timestamp in ms of the earliest trade to fetch
     * @param {int} [limit] the maximum number of trades to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction trade structures](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    async fetchTrades (outcome: Str, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionTrade[]> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const slug = this.safeString (outcomeObj['info'], 'slug');
        const tokenId = this.safeString (outcomeObj, 'outcomeId');
        const request: Dict = {
            'slug': slug,
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.limitlessPublicGetMarketsSlugEvents (this.extend (request, params));
        //
        //     {
        //         "events": [
        //             {
        //                 "createdAt": "2026-06-12T15:24:25.617Z",
        //                 "makerAmount": "19996200",
        //                 "matchedSize": "2500000",
        //                 "price": 0.332,
        //                 "profile": { "account": "0x0572B4Aa431e730d1d19cc7CFea7D6C0Bc07096f" },
        //                 "side": 0,
        //                 "takerAmount": "830000",
        //                 "title": "",
        //                 "tokenId": "34504808738227095158010759634880534083339296329182698784451980951930485131851",
        //                 "txHash": "0x432344fc63f26f37e3ffe3aca45bcfcba6b7addfc07d04de4410855c0a56a175"
        //             }
        //         ],
        //         "limit": 30,
        //         "page": 1,
        //         "totalPages": 1,
        //         "totalRows": 13
        //     }
        //
        const rows = this.safeList (response, 'events', []) as any[];
        const filtered: any[] = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowTokenId = this.safeString (row, 'tokenId');
            if ((tokenId !== undefined) && (rowTokenId !== undefined) && (rowTokenId !== tokenId)) {
                continue;
            }
            filtered.push (row);
        }
        return this.parsePredictionTrades (filtered, outcomeObj, since, limit);
    }

    /**
     * @method
     * @name limitless#fetchOrderBook
     * @description fetches the order book for a single outcome token, converting 6-decimal USDC sizes to whole units, no outcomes are quoted at 1 - price with the sides swapped
     * @see https://docs.limitless.exchange/api-reference/trading/orderbook
     * @param {string} outcome unified outcome like TRUMP_OUT_PRESIDENT_2027:YES or an outcome token id
     * @param {int} [limit] not used by limitless fetchOrderBook
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction order book structure](https://docs.ccxt.com/#/?id=prediction-order-book-structure)
     */
    async fetchOrderBook (outcome: Str, limit: Int = undefined, params = {}): Promise<PredictionOrderBook> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const slug = this.safeString (outcomeObj['info'], 'slug');
        const request: Dict = {
            'slug': slug,
        };
        const response = await this.limitlessPublicGetMarketsSlugOrderbook (this.extend (request, params));
        //
        //     {
        //         "bids": [
        //             { "price": "0.14", "size": "12360330000", "side": "BUY" },
        //             { "price": "0.1", "size": "1000000", "side": "BUY" },
        //             { "price": "0.003", "size": "500000000", "side": "BUY" }
        //         ],
        //         "asks": [
        //             { "price": "0.161", "size": "222000000", "side": "SELL" },
        //             { "price": "0.996", "size": "5555000000", "side": "SELL" },
        //             { "price": "0.997", "size": "500000000", "side": "SELL" }
        //         ],
        //         "tokenId": "56154308742753982686710750162015444986563701968079760676518531584453506363044",
        //         "adjustedMidpoint": "0.1505",
        //         "midpoint": "0.1505",
        //         "maxSpread": "0.035",
        //         "minSize": "100000000",
        //         "lastTradePrice": "0.161"
        //     }
        //
        const timestamp = this.milliseconds ();
        const decimals = this.safeInteger (this.options, 'usdcDecimals', 6);
        // sizes are scaled by 10^decimals, USDC uses 6 decimals
        const scaleStr = this.parsePrecision (this.numberToString (-decimals));
        const outcomeLabel = this.safeStringLower (outcomeObj['info'], 'outcomeLabel', 'yes');
        const isYes = outcomeLabel !== 'no';
        const rawBids = this.safeList (response, 'bids', []) as any[];
        const rawAsks = this.safeList (response, 'asks', []) as any[];
        // the book endpoint is quoted in the yes token, the no side mirrors at 1 - price with bids and asks swapped
        const bidsSource = (isYes) ? rawBids : rawAsks;
        const asksSource = (isYes) ? rawAsks : rawBids;
        const bids: any[] = [];
        const asks: any[] = [];
        for (let bi = 0; bi < bidsSource.length; bi++) {
            let priceStr = this.safeString (bidsSource[bi], 'price');
            if (!isYes && (priceStr !== undefined)) {
                priceStr = Precise.stringSub ('1', priceStr);
            }
            let sizeStr = this.safeString (bidsSource[bi], 'size');
            if (sizeStr !== undefined) {
                sizeStr = Precise.stringDiv (sizeStr, scaleStr);
            }
            bids.push ([ this.parseNumber (priceStr), this.parseNumber (sizeStr) ]);
        }
        for (let ai = 0; ai < asksSource.length; ai++) {
            let priceStr = this.safeString (asksSource[ai], 'price');
            if (!isYes && (priceStr !== undefined)) {
                priceStr = Precise.stringSub ('1', priceStr);
            }
            let sizeStr = this.safeString (asksSource[ai], 'size');
            if (sizeStr !== undefined) {
                sizeStr = Precise.stringDiv (sizeStr, scaleStr);
            }
            asks.push ([ this.parseNumber (priceStr), this.parseNumber (sizeStr) ]);
        }
        const orderbook = {
            'outcome': this.safeOutcomeSymbol (outcome, outcomeObj),
            'bids': this.sortBy (bids, 0, true),
            'asks': this.sortBy (asks, 0),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'nonce': undefined,
        };
        return this.safePredictionOrderBook (orderbook, outcomeObj);
    }

    /**
     * @method
     * @name limitless#fetchOHLCV
     * @description fetches historical prices for a single limitless market outcome and maps them to OHLCV format, uses the `interval` query parameter and selects the YES/NO series that matches the requested outcome
     * @see https://docs.limitless.exchange/api-reference/trading/historical-price
     * @param {string} outcome outcome, e.g. "TRUMP_OUT:YES"
     * @param {string} timeframe the length of time each candle represents
     * @param {int} [since] timestamp in ms of the earliest candle to fetch
     * @param {int} [limit] the maximum number of candles to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {int[][]} a list of candles ordered as timestamp, open, high, low, close, volume
     */
    async fetchOHLCV (outcome: Str, timeframe = '1d', since: Int = undefined, limit: Int = undefined, params = {}): Promise<OHLCV[]> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const slug = this.safeString (outcomeObj['info'], 'slug');
        const outcomeLabel = this.safeStringUpper (outcomeObj['info'], 'outcomeLabel');
        const interval = this.safeString (this.timeframes, timeframe, '1d');
        const response = await this.limitlessPublicGetMarketsSlugHistoricalPrice (this.extend ({
            'slug': slug,
            'interval': interval,
        }, params));
        //
        //     {
        //         "data": [
        //             {
        //                  "timestamp": 1705318200000,
        //                  "price": 0.1655
        //             },
        //         ]
        //     }
        //
        //
        //     [
        //         {
        //             "title": "YES Token",
        //             "prices": [
        //                 {
        //                     "price": 0.75,
        //                     "timestamp": "2024-01-15T10:30:00Z"
        //                 },
        //             ]
        //         },
        //         {
        //             "title": "NO Token",
        //             "prices": [
        //                 {
        //                     "price": 0.25,
        //                     "timestamp": "2024-01-15T10:30:00Z"
        //                 }
        //             ]
        //         }
        //     ]
        //
        const rawHistoryList = this.safeList (response, 'data', this.safeList (response, 'prices', response as any));
        const rawHistory = (rawHistoryList !== undefined) ? rawHistoryList : [];
        let history: any[] = rawHistory;
        const rawHistoryLength = rawHistory.length;
        if (rawHistoryLength > 0) {
            const first = this.safeDict (rawHistory, 0, {});
            const firstPrices = this.safeList (first, 'prices');
            if (firstPrices !== undefined) {
                let selectedSeries = first;
                for (let i = 0; i < rawHistory.length; i++) {
                    const series = this.safeDict (rawHistory, i, {});
                    const title = this.safeStringUpper (series, 'title', '');
                    if ((outcomeLabel !== undefined) && (title.indexOf (outcomeLabel) >= 0)) {
                        selectedSeries = series;
                        break;
                    }
                }
                history = this.safeList (selectedSeries, 'prices', []);
            }
        }
        // the endpoint returns raw price points, not candles - bucket them into
        // timeframe-aligned candles (single points would carry unaligned timestamps)
        const pseudoTrades = [];
        for (let i = 0; i < history.length; i++) {
            const point = history[i];
            const pointPrice = this.safeNumber (point, 'price');
            let pointTs = this.safeInteger (point, 'timestamp');
            if (pointTs === undefined) {
                const tsString = this.safeString (point, 'timestamp');
                pointTs = tsString ? this.parse8601 (tsString) : undefined;
            } else if (pointTs < 1000000000000) {
                // old responses may return unix seconds
                pointTs = pointTs * 1000;
            }
            if ((pointPrice !== undefined) && (pointTs !== undefined)) {
                pseudoTrades.push ({ 'timestamp': pointTs, 'price': pointPrice, 'amount': 0 });
            }
        }
        // the endpoint returns points NEWEST-first, so sort ascending by timestamp before bucketing —
        // otherwise candles come back descending and open/close are inverted within each bucket
        // — the first point seen would be the latest, not the earliest. sortBy is stable, so equal
        // timestamps keep their relative order consistently across languages
        const sorted = this.sortBy (pseudoTrades, 'timestamp');
        const ms = this.parseTimeframe (timeframe) * 1000;
        const candles: Dict = {};
        const bucketOrder = [];
        for (let i = 0; i < sorted.length; i++) {
            const point = sorted[i];
            const pTs = this.safeInteger (point, 'timestamp');
            const pPrice = this.safeNumber (point, 'price');
            const bucket = this.parseToInt (pTs / ms) * ms;
            const key = bucket.toString ();
            if (!(key in candles)) {
                candles[key] = [ bucket, pPrice, pPrice, pPrice, pPrice, 0 ];
                bucketOrder.push (key);
            } else {
                const candle = candles[key];
                candle[2] = Math.max (candle[2], pPrice);
                candle[3] = Math.min (candle[3], pPrice);
                candle[4] = pPrice;
                candles[key] = candle; // php arrays are value types - write the mutation back
            }
        }
        const result = [];
        for (let i = 0; i < bucketOrder.length; i++) {
            result.push (candles[bucketOrder[i]]);
        }
        return this.filterBySinceLimit (result, since, limit, 0) as OHLCV[];
    }

    /**
     * @method
     * @name limitless#fetchOrders
     * @description fetches orders for the authenticated user for a single outcome
     * @see https://docs.limitless.exchange/api-reference/orders/get-user-orders
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {int} [since] the earliest time in ms to fetch orders for
     * @param {int} [limit] the maximum number of order structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOrders (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOrders requires an outcome argument');
        }
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const info = this.safeDict (outcomeObj, 'info');
        const request: Dict = {
            'slug': this.safeString (info, 'slug'),
            'statuses': [ 'LIVE', 'MATCHED' ],
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.limitlessPrivateGetMarketsSlugUserOrders (this.extend (request, params));
        //
        //     [
        //         {
        //             "createdAt": "2026-05-04T08:57:06.448Z",
        //             "id": "c4b1a83a-219f-48db-a9be-1ddadf0bc14c",
        //             "ownerId": 1315134,
        //             "marketId": "112523",
        //             "token": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //             "type": "GTC",
        //             "status": "LIVE",
        //             "side": "BUY",
        //             "makerAmount": "1000040",
        //             "takerAmount": "10870000",
        //             "price": "0.092",
        //             "originalSize": "10870000",
        //             "remainingSize": "10870000"
        //         }
        //     ]
        //
        // pass undefined as market: parsePredictionOrder sets outcome to the market outcome while the outcome
        // lives under 'outcome', so the base outcome filter would drop every order; the per-slug
        // endpoint already scopes results and parsePredictionOrder resolves the outcome via outcomes_by_id
        return this.parsePredictionOrders (response, undefined, since, limit);
    }

    /**
     * @method
     * @name limitless#fetchOpenOrders
     * @description fetches open orders for the authenticated user for a single outcome
     * @see https://docs.limitless.exchange/api-reference/orders/get-user-orders
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {int} [since] the earliest time in ms to fetch orders for
     * @param {int} [limit] the maximum number of order structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOpenOrders (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOpenOrders requires an outcome argument');
        }
        await this.loadOutcome (outcome);
        params = this.extend (params, {
            'statuses': [ 'LIVE' ],
        });
        return await this.fetchOrders (outcome, since, limit, params);
    }

    /**
     * @method
     * @name limitless#fetchClosedOrders
     * @description fetches closed orders for the authenticated user for a single outcome
     * @see https://docs.limitless.exchange/api-reference/orders/get-user-orders
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {int} [since] the earliest time in ms to fetch orders for
     * @param {int} [limit] the maximum number of order structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchClosedOrders (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchClosedOrders requires an outcome argument');
        }
        await this.loadOutcome (outcome);
        params = this.extend (params, {
            'statuses': [ 'MATCHED' ],
        });
        return await this.fetchOrders (outcome, since, limit, params);
    }

    /**
     * @method
     * @name limitless#fetchOrdersByIds
     * @description fetch orders by the list of order id
     * @see https://docs.limitless.exchange/api-reference/trading/order-status-batch
     * @param {string[]} ids list of order id
     * @param {string} [outcome] market outcome, e.g. "TRUMP_OUT:YES"
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOrdersByIds (ids, outcome: Str = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
        }
        const length = ids.length;
        if (length > 50) {
            throw new BadRequest (this.id + ' fetchOrdersByIds can only fetch up to 50 orders at a time');
        }
        const items: Dict[] = [];
        for (let i = 0; i < length; i++) {
            const id = this.safeString (ids, i);
            const item: Dict = {
                'orderId': id,
            };
            items.push (item);
        }
        const request: Dict = {
            'items': items,
        };
        const response = await this.limitlessPrivatePostOrdersStatusBatch (this.extend (request, params));
        //
        //     {
        //         "results": [
        //             {
        //                 "index": 0,
        //                 "status": "found",
        //                 "orderId": "ff0dcbf1-f7de-43f0-b6f1-16b972a17f49",
        //                 "data": {
        //                     "order": {
        //                         "createdAt": "2026-05-04T10:26:01.334Z",
        //                         "id": "ff0dcbf1-f7de-43f0-b6f1-16b972a17f49",
        //                         "makerAmount": "9999360",
        //                         "takerAmount": "10752000",
        //                         "expiration": null,
        //                         "signatureType": 2,
        //                         "salt": "277966495716",
        //                         "maker": "0xAb2B9833FC8B8f55F4De7C4A0FAb8577EF0F7b36",
        //                         "signer": "0x82a0f074C6C0C11aA370D7FBF077668c31fCc990",
        //                         "taker": "0x0000000000000000000000000000000000000000",
        //                         "tokenId": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //                         "side": 0,
        //                         "feeRateBps": 300,
        //                         "nonce": "0",
        //                         "signature": "0x1640c8558d8c627017b2c6ac71a79770d177033afeb9be72842803afcd12938f23308c507935011ad6d10acc299e5367f76b4f806eb025c15cb05739de11260d1b",
        //                         "orderType": "FAK",
        //                         "price": "0.93",
        //                         "marketId": 112523,
        //                         "ownerId": 1315134,
        //                         "market": {
        //                             "id": 112523,
        //                             "slug": "doge-above-dollar010859-on-may-4-2000-utc-1777838401426",
        //                             "title": "DOGE above $0.10859 on May 4, 20:00 UTC?",
        //                             "status": "FUNDED",
        //                             "yesPositionId": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //                             "noPositionId": "101714389600295994108208140228744407174156865974966685440205519669272948152879"
        //                         },
        //                         "owner": {
        //                             "id": 1315134,
        //                             "account": "0x7CFF82f72b991B6B2b661e404389fD8a40bCD21B",
        //                             "client": "eoa",
        //                             "tradeWalletOption": "smartWallet",
        //                             "smartWallet": "0xAb2B9833FC8B8f55F4De7C4A0FAb8577EF0F7b36",
        //                             "points": 0,
        //                             "referredUsersCount": 0
        //                         }
        //                     },
        //                     "makerMatches": [
        //                         {
        //                             "id": "be44a183-ec56-4076-a69c-10283321abd6",
        //                             "matchedSize": "10752000",
        //                             "fillPrice": "0.93",
        //                             "fillCost": "9999360",
        //                             "orderId": "3255c786-3f30-4115-a99f-72be478f2e44",
        //                             "order": {
        //                                 "id": "3255c786-3f30-4115-a99f-72be478f2e44",
        //                                 "maker": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                                 "price": "0.07",
        //                                 "side": 0,
        //                                 "tokenId": "101714389600295994108208140228744407174156865974966685440205519669272948152879",
        //                                 "owner": {
        //                                     "id": 202602,
        //                                     "account": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                                     "client": "eoa",
        //                                     "tradeWalletOption": null,
        //                                     "smartWallet": null,
        //                                     "username": null,
        //                                     "displayName": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                                     "pfpUrl": null,
        //                                     "socialUrl": null,
        //                                     "points": 0,
        //                                     "referredUsersCount": 0
        //                                 }
        //                             }
        //                         }
        //                     ],
        //                     "execution": {
        //                         "feeRateBps": 300,
        //                         "effectiveFeeBps": 59,
        //                         "matched": true,
        //                         "settlementStatus": "MINED",
        //                         "tradeEventId": "44c46a93-f5cb-40f5-a52f-bd55bb97641e",
        //                         "txHash": "0x101cda4b605007440b382c35a27531605c7fc1b29a7c803b19237586a74c10e8",
        //                         "totalsRaw": {
        //                             "contractsGross": "10752000",
        //                             "contractsFee": "63436",
        //                             "contractsNet": "10688564",
        //                             "usdGross": "9999360",
        //                             "usdFee": "0",
        //                             "usdNet": "9999360"
        //                         }
        //                     }
        //                 }
        //             }
        //         ]
        //     }
        //
        const results = this.safeList (response, 'results', []);
        const found = [];
        for (let i = 0; i < results.length; i++) {
            const item = this.safeDict (results, i, {});
            const itemStatus = this.safeString (item, 'status');
            if (itemStatus === 'found') {
                found.push (item);
            }
        }
        return this.parsePredictionOrders (found);
    }

    /**
     * @method
     * @name limitless#fetchOrder
     * @description fetches information on an order made by the user
     * @see https://docs.limitless.exchange/api-reference/trading/order-status-batch
     * @param {string} id the order id
     * @param {string} [outcome] market outcome, e.g. "TRUMP_OUT:YES"
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOrder (id: string, outcome: Str = undefined, params = {}): Promise<PredictionOrder> {
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
        }
        const orders = await this.fetchOrdersByIds ([ id ], outcome, params);
        const order = this.safeDict (orders, 0);
        if (order === undefined) {
            throw new OrderNotFound (this.id + ' fetchOrder() could not find order ' + id);
        }
        return order as PredictionOrder;
    }

    /**
     * @ignore
     * @method
     * @name limitless#parsePredictionOrder
     * @description parses a raw limitless order object into a unified order object
     * @param {object} order the raw order object
     * @param {object} [market] the outcome object the order belongs to
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    parsePredictionOrder (order: Dict, market: Market = undefined): PredictionOrder {
        //
        // fetchOrders, fetchOpenOrders, fetchClosedOrders
        //     {
        //         "createdAt": "2026-05-04T08:57:06.448Z",
        //         "id": "c4b1a83a-219f-48db-a9be-1ddadf0bc14c",
        //         "ownerId": 1315134,
        //         "marketId": "112523",
        //         "token": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //         "type": "GTC",
        //         "status": "LIVE",
        //         "side": "BUY",
        //         "makerAmount": "1000040",
        //         "takerAmount": "10870000",
        //         "price": "0.092",
        //         "originalSize": "10870000",
        //         "remainingSize": "10870000"
        //     }
        //
        // fetchOrdersByIds, fetchOrder
        //     {
        //         "index": 0,
        //         "status": "found",
        //         "orderId": "ff0dcbf1-f7de-43f0-b6f1-16b972a17f49",
        //         "data": {
        //             "order": {
        //                 "createdAt": "2026-05-04T10:26:01.334Z",
        //                 "id": "ff0dcbf1-f7de-43f0-b6f1-16b972a17f49",
        //                 "makerAmount": "9999360",
        //                 "takerAmount": "10752000",
        //                 "expiration": null,
        //                 "signatureType": 2,
        //                 "salt": "277966495716",
        //                 "maker": "0xAb2B9833FC8B8f55F4De7C4A0FAb8577EF0F7b36",
        //                 "signer": "0x82a0f074C6C0C11aA370D7FBF077668c31fCc990",
        //                 "taker": "0x0000000000000000000000000000000000000000",
        //                 "tokenId": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //                 "side": 0,
        //                 "feeRateBps": 300,
        //                 "nonce": "0",
        //                 "signature": "0x1640c8558d8c627017b2c6ac71a79770d177033afeb9be72842803afcd12938f23308c507935011ad6d10acc299e5367f76b4f806eb025c15cb05739de11260d1b",
        //                 "orderType": "FAK",
        //                 "price": "0.93",
        //                 "marketId": 112523,
        //                 "ownerId": 1315134,
        //                 "market": {
        //                     "id": 112523,
        //                     "slug": "doge-above-dollar010859-on-may-4-2000-utc-1777838401426",
        //                     "title": "DOGE above $0.10859 on May 4, 20:00 UTC?",
        //                     "status": "FUNDED",
        //                     "yesPositionId": "46235703925185836960484608024734446969378108670784413458211837874003718039438",
        //                     "noPositionId": "101714389600295994108208140228744407174156865974966685440205519669272948152879"
        //                 },
        //                 "owner": {
        //                     "id": 1315134,
        //                     "account": "0x7CFF82f72b991B6B2b661e404389fD8a40bCD21B",
        //                     "client": "eoa",
        //                     "tradeWalletOption": "smartWallet",
        //                     "smartWallet": "0xAb2B9833FC8B8f55F4De7C4A0FAb8577EF0F7b36",
        //                     "points": 0,
        //                     "referredUsersCount": 0
        //                 }
        //             },
        //             "makerMatches": [
        //                 {
        //                     "id": "be44a183-ec56-4076-a69c-10283321abd6",
        //                     "matchedSize": "10752000",
        //                     "fillPrice": "0.93",
        //                     "fillCost": "9999360",
        //                     "orderId": "3255c786-3f30-4115-a99f-72be478f2e44",
        //                     "order": {
        //                         "id": "3255c786-3f30-4115-a99f-72be478f2e44",
        //                         "maker": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                         "price": "0.07",
        //                         "side": 0,
        //                         "tokenId": "101714389600295994108208140228744407174156865974966685440205519669272948152879",
        //                         "owner": {
        //                             "id": 202602,
        //                             "account": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                             "client": "eoa",
        //                             "tradeWalletOption": null,
        //                             "smartWallet": null,
        //                             "username": null,
        //                             "displayName": "0x8274600A3a9DC84747E6dEb49380F6B1DE2C505d",
        //                             "pfpUrl": null,
        //                             "socialUrl": null,
        //                             "points": 0,
        //                             "referredUsersCount": 0
        //                         }
        //                     }
        //                 }
        //             ],
        //             "execution": {
        //                 "feeRateBps": 300,
        //                 "effectiveFeeBps": 59,
        //                 "matched": true,
        //                 "settlementStatus": "MINED",
        //                 "tradeEventId": "44c46a93-f5cb-40f5-a52f-bd55bb97641e",
        //                 "txHash": "0x101cda4b605007440b382c35a27531605c7fc1b29a7c803b19237586a74c10e8",
        //                 "totalsRaw": {
        //                     "contractsGross": "10752000",
        //                     "contractsFee": "63436",
        //                     "contractsNet": "10688564",
        //                     "usdGross": "9999360",
        //                     "usdFee": "0",
        //                     "usdNet": "9999360"
        //                 }
        //             }
        //         }
        //     }
        const data = this.safeDict (order, 'data');
        let rawOrder = this.safeDict (data, 'order', order);
        // createOrder returns the order nested under an 'order' key
        const wrappedOrder = this.safeDict (rawOrder, 'order');
        if (wrappedOrder !== undefined) {
            rawOrder = wrappedOrder;
        }
        const id = this.safeString (rawOrder, 'id');
        const tokenId = this.safeString2 (rawOrder, 'token', 'tokenId');
        const mkt = this.safeOutcome (tokenId, market as any);
        const outcomeSymbol = this.safeString (mkt, 'outcome');
        const rawSide = this.safeString (rawOrder, 'side');
        const side = this.parseOrderSide (rawSide);
        const price = this.safeString (rawOrder, 'price');
        const amountKey = (side === 'buy') ? 'takerAmount' : 'makerAmount'; // todo check
        const amount = this.safeString (rawOrder, amountKey);
        const remaining = this.safeString (rawOrder, 'remainingSize');
        const datetime = this.safeString (rawOrder, 'createdAt');
        const ts = this.parse8601 (datetime);
        const timeInForce = this.safeString2 (rawOrder, 'type', 'orderType');
        let type = undefined;
        if (timeInForce === 'GTC') {
            type = 'limit';
        } else if (timeInForce === 'FAK') {
            type = 'market';
        }
        let rawStatus = this.safeString (rawOrder, 'status');
        const execution = this.safeDict (data, 'execution');
        let fee = undefined;
        let filled = undefined;
        let cost = undefined;
        if (execution !== undefined) {
            rawStatus = this.safeString (execution, 'settlementStatus');
            const totals = this.safeDict (execution, 'totalsRaw');
            cost = this.safeString (totals, 'usdGross');
            filled = this.safeString (totals, 'contractsGross');
            let feeCurrency = 'USDC';
            let feeCost = this.safeString (totals, 'usdFee');
            if (side === 'buy') {
                feeCurrency = outcomeSymbol;
                feeCost = this.safeString (totals, 'contractsFee');
            }
            fee = {
                'cost': this.applyScale (feeCost),
                'currency': feeCurrency,
            };
        }
        return this.safePredictionOrder ({
            'id': id,
            'clientOrderId': undefined,
            'info': order,
            'timestamp': ts,
            'datetime': datetime,
            'lastTradeTimestamp': undefined,
            'status': this.parseOrderStatus (rawStatus),
            'outcome': outcomeSymbol,
            'outcomeId': this.safeString (mkt, 'outcomeId'),
            'label': this.safeString (mkt, 'label'),
            'market': this.safeString (mkt, 'market'),
            'type': type,
            'timeInForce': this.parseOrderTimeInForce (timeInForce),
            'postOnly': undefined,
            'side': side,
            'price': price,
            'stopPrice': undefined,
            'triggerPrice': undefined,
            'average': undefined,
            'amount': this.applyScale (amount),
            'cost': this.applyScale (cost),
            'filled': this.applyScale (filled),
            'remaining': this.applyScale (remaining),
            'fee': fee,
            'trades': [],
        });
    }

    /**
     * @ignore
     * @method
     * @name limitless#parseOrderStatus
     * @description maps an order status string to the CCXT unified status vocabulary
     * @param {string} status the raw limitless order status
     * @returns {string} the unified order status
     */
    parseOrderStatus (status: Str): Str {
        const statuses: Dict = {
            'LIVE': 'open',
            'MATCHED': 'closed',
            // 'UNMATCHED': 'open', - both open and closed orders can have unmatched status, so we can't reliably map it to one or the other
            'PENDING': 'pending',
            'MINED': 'closed',
            'CONFIRMED': 'closed',
            'FAILED': 'rejected',
        };
        return this.safeString (statuses, status, status);
    }

    /**
     * @ignore
     * @method
     * @name limitless#parseOrderTimeInForce
     * @description maps an order time in force string to the CCXT unified type vocabulary
     * @param {string} timeInForce the raw limitless time in force
     * @returns {string} the unified time in force
     */
    parseOrderTimeInForce (timeInForce: Str): Str {
        const timeInForces: Dict = {
            'FAK': 'FOK',
        };
        return this.safeString (timeInForces, timeInForce, timeInForce);
    }

    /**
     * @ignore
     * @method
     * @name limitless#parseOrderSide
     * @description maps an order side string to the CCXT unified side vocabulary
     * @param {string} side the raw limitless order side
     * @returns {string} the unified order side
     */
    parseOrderSide (side: Str): Str {
        const sides: Dict = {
            'BUY': 'buy',
            'SELL': 'sell',
            '0': 'buy',
            '1': 'sell',
        };
        return this.safeString (sides, side, side);
    }

    applyScale (amount: Str, multiply: Bool = false): Str {
        const decimals = this.safeInteger (this.options, 'usdcDecimals', 6);
        const scale = this.numberToString (Math.pow (10, decimals));
        if (multiply) {
            return Precise.stringMul (amount, scale);
        } else {
            return Precise.stringDiv (amount, scale);
        }
    }

    parseAccount (account: Dict): Account {
        const accountId = this.safeString (account, 'id');
        return {
            'id': accountId,
            'type': undefined,
            'code': undefined,
            'info': account,
        };
    }

    /**
     * @method
     * @name limitless#fetchAccounts
     * @description query for account id and info
     * @see https://docs.limitless.exchange/api-reference/portfolio/get-current-profile
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [account structures]
     */
    async fetchAccounts (params = {}): Promise<Account[]> {
        const response = await this.limitlessPrivateGetProfilesMe (params);
        const responseList = [ response ];
        return this.parseAccounts (responseList);
    }

    /**
     * @method
     * @name limitless#createOrder
     * @description places a limit or market order on limitless for the given outcome token
     * @see https://docs.limitless.exchange/api-reference/orders/create-order
     * @param {string} outcome outcome, e.g. "TRUMP_OUT:YES"
     * @param {string} type 'limit' or 'market'
     * @param {string} side 'buy' or 'sell'
     * @param {float} amount amount of outcome tokens
     * @param {float} [price] limit price (0–1 range)
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async createOrder (outcome: string, type: Str, side: Str, amount: Num, price: Num = undefined, params = {}): Promise<PredictionOrder> {
        const accounts = await this.loadAccounts ();
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const account = this.safeDict (accounts, 0);
        const accountInfo = this.safeDict (account, 'info');
        // the trade wallet is chosen by `tradeWalletOption`: 'smartWallet' profiles trade through
        // the `smartWallet` address, plain 'eoa' profiles trade directly from `account`. the
        // smartWallet field can stay populated after switching to eoa, so key off the option here
        const tradeWalletOption = this.safeString (accountInfo, 'tradeWalletOption');
        const usesSmartWallet = (tradeWalletOption === 'smartWallet');
        const walletFromAccount = (usesSmartWallet) ? this.safeString (accountInfo, 'smartWallet') : this.safeString (accountInfo, 'account');
        let maker = this.walletAddress ? this.walletAddress : walletFromAccount;
        [ maker, params ] = this.handleOptionAndParams (params, 'createOrder', 'maker', maker);
        try {
            this.checkAddress (maker);
        } catch (e) {
            throw new InvalidAddress (this.id + ' createOrder requires a valid maker address. Set the "maker" parameter to a valid address or set the "walletAddress" property in the constructor options.');
        }
        // when the profile trades through a smart wallet the order must be signed by the
        // linked embedded (owner) wallet, not by the smart wallet itself
        const embeddedAddress = this.safeString (accountInfo, 'embeddedAccount');
        const hasEmbedded = (embeddedAddress !== undefined);
        const isSmartWallet = usesSmartWallet && hasEmbedded;
        let signer = maker;
        if (isSmartWallet) {
            signer = embeddedAddress;
        }
        [ signer, params ] = this.handleOptionAndParams (params, 'createOrder', 'signer', signer);
        try {
            this.checkAddress (signer);
        } catch (e) {
            throw new InvalidAddress (this.id + ' createOrder requires a valid signer address. Set the "signer" parameter to a valid address or set the "walletAddress" property in the constructor options.');
        }
        let taker = this.safeString (this.options, 'nullAddress', '0x0000000000000000000000000000000000000000');
        [ taker, params ] = this.handleOptionAndParams (params, 'createOrder', 'taker', taker);
        try {
            this.checkAddress (taker);
        } catch (e) {
            throw new InvalidAddress (this.id + ' createOrder requires a valid taker address. Set the "taker" parameter to a valid address or set the "nullAddress" property in the constructor options.');
        }
        const nonce = this.milliseconds ();
        const sides: Dict = {
            'buy': 0,
            'sell': 1,
        };
        const sideValue = this.safeInteger (sides, side.toLowerCase ());
        const rank = this.safeDict (accountInfo, 'rank');
        // signatureType: 0 = EOA, 2 = smart-wallet (the embedded owner signs on behalf of the safe)
        let signatureType = isSmartWallet ? 2 : 0;
        [ signatureType, params ] = this.handleOptionAndParams (params, 'createOrder', 'signatureType', signatureType);
        const signRequest: Dict = {
            'salt': nonce,
            'maker': maker,
            'signer': signer,
            'taker': taker,
            'tokenId': outcomeObj['outcomeId'],
            'nonce': 0,
            'feeRateBps': this.safeInteger (rank, 'feeRateBps', 0),
            'side': sideValue,
            'signatureType': signatureType,
        };
        // the contract expects expiration as a uint256; non-zero values are rejected by the API (GTC orders use 0)
        const expirationInt = this.safeInteger (params, 'expiration');
        if (expirationInt !== undefined) {
            params = this.omit (params, 'expiration');
            signRequest['expiration'] = this.numberToString (expirationInt);
        } else {
            signRequest['expiration'] = '0';
        }
        const amountString = this.numberToString (amount);
        const priceString = this.numberToString (price);
        let makerAmount = undefined;
        let takerAmount = undefined;
        const isMarket = type === 'market';
        let postOnly = false;
        [ postOnly, params ] = this.handlePostOnly (isMarket, false, params);
        let timeInForce = this.safeString (params, 'timeInForce');
        params = this.omit (params, 'timeInForce');
        if (timeInForce === undefined) {
            timeInForce = isMarket ? 'FOK' : 'GTC';
        }
        const marketSymbol = this.safeString (outcomeObj, 'market');
        if (isMarket && (side === 'buy')) {
            let createMarketBuyOrderRequiresPrice = true;
            [ createMarketBuyOrderRequiresPrice, params ] = this.handleOptionAndParams (params, 'createOrder', 'createMarketBuyOrderRequiresPrice', true);
            const cost = this.safeNumber (params, 'cost');
            params = this.omit (params, 'cost');
            if (createMarketBuyOrderRequiresPrice) {
                if ((price === undefined) && (cost === undefined)) {
                    throw new InvalidOrder (this.id + ' createOrder() requires the price argument for market buy orders to calculate the total cost to spend (amount * price), alternatively set the createMarketBuyOrderRequiresPrice option or param to false and pass the cost to spend in the amount argument');
                } else {
                    const quoteAmount = this.parseToNumeric (Precise.stringMul (amountString, priceString));
                    const costRequest = (cost !== undefined) ? cost : quoteAmount;
                    makerAmount = this.costToPredictionPrecision (outcome, costRequest);
                }
            } else {
                makerAmount = this.costToPredictionPrecision (outcome, amount);
            }
        } else if (isMarket) {
            makerAmount = this.amountToPredictionPrecision (outcome, amount);
        } else {
            const calculatedCost = Precise.stringMul (amountString, priceString);
            if (side === 'buy') {
                makerAmount = this.costToPredictionPrecision (outcome, calculatedCost);
                takerAmount = this.amountToPredictionPrecision (outcome, amount);
            } else {
                makerAmount = this.amountToPredictionPrecision (outcome, amount);
                takerAmount = this.costToPredictionPrecision (outcome, calculatedCost);
            }
        }
        // amounts must be integers (uint256): parseNumber yields a float that the Python EIP-712 encoder rejects
        signRequest['makerAmount'] = this.parseToInt (this.applyScale (makerAmount, true));
        signRequest['takerAmount'] = isMarket ? 1 : this.parseToInt (this.applyScale (takerAmount, true));
        const signature = this.signOrderRequest (signRequest, marketSymbol);
        signRequest['signature'] = signature;
        // price is an unsigned hint required by the API for GTC/FAK orders (not part of the EIP-712 struct)
        if (!isMarket && (price !== undefined)) {
            signRequest['price'] = this.parseNumber (priceString);
        }
        const slug = this.safeString (outcomeObj['info'], 'slug');
        const request: Dict = {
            'ownerId': this.safeInteger (account, 'id'),
            'order': signRequest,
            'marketSlug': slug,
            'orderType': timeInForce,
        };
        if (postOnly) {
            request['postOnly'] = postOnly;
        }
        const response = await this.limitlessPrivatePostOrders (this.extend (request, params));
        const parsedOrder = this.parsePredictionOrder (response, outcomeObj as any);
        // the create-order response omits a status field; a freshly accepted order is open
        if (parsedOrder['status'] === undefined) {
            parsedOrder['status'] = 'open';
        }
        return parsedOrder;
    }

    signOrderRequest (signRequest: Dict, marketSymbol) {
        this.checkRequiredCredentials ();
        if (this.privateKey === undefined) {
            throw new ArgumentsRequired (this.id + ' createOrder() requires a privateKey (the embedded/trading wallet key) to sign orders');
        }
        const market = this.market (marketSymbol);
        const info = this.safeDict (market, 'info');
        const venue = this.safeDict (info, 'venue');
        const exchange = this.safeString (venue, 'exchange');
        const domain: Dict = {
            'chainId': 8453,
            'name': 'Limitless CTF Exchange',
            'verifyingContract': exchange,
            'version': '1',
        };
        const messageTypes: Dict = {
            'Order': [
                { 'name': 'salt', 'type': 'uint256' },
                { 'name': 'maker', 'type': 'address' },
                { 'name': 'signer', 'type': 'address' },
                { 'name': 'taker', 'type': 'address' },
                { 'name': 'tokenId', 'type': 'uint256' },
                { 'name': 'makerAmount', 'type': 'uint256' },
                { 'name': 'takerAmount', 'type': 'uint256' },
                { 'name': 'expiration', 'type': 'uint256' },
                { 'name': 'nonce', 'type': 'uint256' },
                { 'name': 'feeRateBps', 'type': 'uint256' },
                { 'name': 'side', 'type': 'uint8' },
                { 'name': 'signatureType', 'type': 'uint8' },
            ],
        };
        const msg = this.ethEncodeStructuredData (domain, messageTypes, signRequest);
        return this.signMessage (msg, this.privateKey);
    }

    hashMessage (message) {
        return '0x' + this.hash (message, keccak, 'hex');
    }

    signHash (hash, privateKey) {
        const signature = ecdsa (hash.slice (-64), privateKey.slice (-64), secp256k1, undefined);
        const r = signature['r'];
        const s = signature['s'];
        const v = this.intToBase16 (this.sum (27, signature['v']));
        const rPadded = r.padStart (64, '0');
        const sPadded = s.padStart (64, '0');
        const result = '0x' + rPadded + sPadded + v;
        return result.toLowerCase ();
    }

    signMessage (message, privateKey) {
        return this.signHash (this.hashMessage (message), privateKey.slice (-64));
    }

    signEvmTransaction (tx: Dict, privateKey: string): string {
        // builds and signs an EIP-1559 (type 0x02) transaction, returning the signed raw tx hex
        const accessList = this.rlpEncodeList ([]);
        const fields = [
            this.rlpEncodeBytes (this.intToRlpHex (this.safeInteger (tx, 'chainId'))),
            this.rlpEncodeBytes (this.hexToRlpBytes (this.safeString (tx, 'nonce'))),
            this.rlpEncodeBytes (this.hexToRlpBytes (this.safeString (tx, 'maxPriorityFeePerGas'))),
            this.rlpEncodeBytes (this.hexToRlpBytes (this.safeString (tx, 'maxFeePerGas'))),
            this.rlpEncodeBytes (this.hexToRlpBytes (this.safeString (tx, 'gasLimit'))),
            this.rlpEncodeBytes (this.remove0xPrefix (this.safeString (tx, 'to'))),
            this.rlpEncodeBytes (this.hexToRlpBytes (this.safeString (tx, 'value', '0x0'))),
            this.rlpEncodeBytes (this.remove0xPrefix (this.safeString (tx, 'data', '0x'))),
            accessList,
        ];
        const payload = '02' + this.rlpEncodeList (fields);
        const hashHex = this.hash (this.base16ToBinary (payload), keccak, 'hex');
        const signature = ecdsa (hashHex, this.remove0xPrefix (privateKey), secp256k1, undefined);
        let rHex = this.safeString (signature, 'r');
        let sHex = this.safeString (signature, 's');
        rHex = this.padHexToEven (rHex);
        sHex = this.padHexToEven (sHex);
        const yParity = this.safeInteger (signature, 'v');
        const signedFields = [];
        for (let i = 0; i < fields.length; i++) {
            signedFields.push (fields[i]);
        }
        signedFields.push (this.rlpEncodeBytes (this.intToRlpHex (yParity)));
        signedFields.push (this.rlpEncodeBytes (rHex));
        signedFields.push (this.rlpEncodeBytes (sHex));
        return '0x02' + this.rlpEncodeList (signedFields);
    }

    /**
     * @method
     * @name limitless#approve
     * @description sets the on-chain ERC20 collateral (USDC) allowance for the limitless exchange contract on Base, which is required before an EOA maker can place orders ("Insufficient collateral allowance" otherwise). Sends a real on-chain transaction signed with the privateKey and waits for the receipt
     * @param {object} [params] extra parameters
     * @param {string} [params.token] the collateral token address (default USDC on Base)
     * @param {string} [params.spender] the exchange contract to approve (default the limitless CTF exchange); read from a market's venue when omitted
     * @param {string} [params.owner] the token holder address (default this.walletAddress or the address derived from the privateKey)
     * @param {float} [params.amount] the allowance in USDC (default: unlimited / maxUint256)
     * @param {string} [params.rpcUrl] the Base RPC url to broadcast through
     * @param {string} [params.gasLimit] gas limit hex for the approve tx (default '0x186a0')
     * @returns {object} the transaction receipt
     */
    async approve (params = {}): Promise<any> {
        this.checkRequiredCredentials ();
        if (this.privateKey === undefined) {
            throw new ArgumentsRequired (this.id + ' approve() requires a privateKey to sign the on-chain transaction');
        }
        const rpcUrl = this.safeString (params, 'rpcUrl', this.safeString (this.options, 'rpcUrl'));
        const chainId = this.safeInteger (this.options, 'chainId', 8453);
        const token = this.safeString (params, 'token', this.safeString (this.options, 'collateralAddress'));
        const spender = this.safeString (params, 'spender', this.safeString (this.options, 'exchangeAddress'));
        let owner = this.safeString (params, 'owner', this.walletAddress);
        if (owner === undefined) {
            owner = this.ethGetAddressFromPrivateKey (this.privateKey);
        }
        const gasLimit = this.safeString (params, 'gasLimit', '0x186a0');
        const maxUint = 'ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
        let amountHex = maxUint;
        const amount = this.safeString (params, 'amount');
        if (amount !== undefined) {
            const decimals = this.safeInteger (this.options, 'usdcDecimals', 6);
            // scale the human USDC amount to base units (amount / 10^-decimals = amount * 10^decimals)
            const scaled = Precise.stringDiv (amount, this.parsePrecision (this.numberToString (decimals)));
            const amountInt = this.parseToInt (scaled);
            const amountBase16 = this.intToBase16 (amountInt);
            amountHex = amountBase16.padStart (64, '0');
        }
        // approve(spender, amount) -> selector 0x095ea7b3
        const approveData = '0x095ea7b3' + this.padHexAddress (spender) + amountHex;
        const txHash = await this.sendEvmTransaction (rpcUrl, chainId, owner, token, '0x0', approveData, gasLimit);
        return await this.waitForTransactionReceipt (rpcUrl, txHash);
    }

    /**
     * @method
     * @name limitless#cancelOrder
     * @description cancels a single open order by id
     * @see https://docs.limitless.exchange/api-reference/orders/cancel-order
     * @param {string} id order id
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async cancelOrder (id: Str, outcome: Str = undefined, params = {}): Promise<PredictionOrder> {
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
        }
        const request: Dict = {
            'order_id': id,
        };
        const response = await this.limitlessPrivateDeleteOrdersOrderId (this.extend (request, params));
        // the delete response carries no order body, so backfill the id and the resulting status
        const order = this.parsePredictionOrder (response);
        if (order['id'] === undefined) {
            order['id'] = id;
        }
        if (order['status'] === undefined) {
            order['status'] = 'canceled';
        }
        return order as PredictionOrder;
    }

    /**
     * @method
     * @name limitless#redeem
     * @description redeem a resolved winning position back to collateral (gasless — the operator settles on-chain)
     * @see https://docs.limitless.exchange/api-reference/portfolio/redeem
     * @param {string} [outcome] a unified outcome on the resolved market to redeem (used to resolve the market conditionId)
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.conditionId] the CTF condition id (bytes32 hex) to redeem directly, instead of resolving it from an outcome
     * @returns {object} the raw redemption response
     */
    async redeem (outcome: Str = undefined, params = {}): Promise<any> {
        let conditionId = this.safeString2 (params, 'conditionId', 'condition_id');
        if (conditionId === undefined) {
            if (outcome === undefined) {
                throw new ArgumentsRequired (this.id + ' redeem() requires an outcome or a params.conditionId');
            }
            await this.loadOutcome (outcome);
            const outcomeObj = this.outcome (outcome);
            conditionId = this.safeString (this.safeDict (outcomeObj, 'info', {}), 'conditionId');
        }
        if (conditionId === undefined) {
            throw new ArgumentsRequired (this.id + ' redeem() could not resolve the market conditionId - pass params.conditionId (a bytes32 hex string)');
        }
        const request: Dict = {
            'conditionId': conditionId,
        };
        const rest = this.omit (params, [ 'conditionId', 'condition_id' ]);
        const response = await this.limitlessPrivatePostPortfolioRedeem (this.extend (request, rest));
        return {
            'info': response,
            'id': conditionId,
            'conditionId': conditionId,
        };
    }

    /**
     * @method
     * @name limitless#cancelOrders
     * @description cancel multiple orders at the same time
     * @see https://docs.limitless.exchange/api-reference/trading/cancel-batch
     * @param {string[]} ids order ids
     * @param {string} [outcome] unified market outcome, default is undefined
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async cancelOrders (ids: string[], outcome: Str = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
        }
        const request: Dict = {
            'orderIds': ids,
        };
        const response = await this.limitlessPrivatePostOrdersCancelBatch (this.extend (request, params));
        const canceled = this.safeList (response, 'canceled', []);
        const failed = this.safeList (response, 'failed', []);
        const failedLethgn = failed.length;
        if (failedLethgn > 0) {
            const message = this.json (response);
            const feedback = this.id + ' cancelOrders failed: ' + message;
            throw new OrderNotFound (feedback);
        }
        return this.parsePredictionOrders (canceled);
    }

    /**
     * @method
     * @name limitless#cancelAllOrders
     * @description cancels all open orders for one market slug
     * @see https://docs.limitless.exchange/api-reference/orders/cancel-all-orders
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.slug] the market slug to cancel all orders for
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async cancelAllOrders (outcome: Str = undefined, params = {}): Promise<PredictionOrder[]> {
        if (outcome !== undefined) {
            let warn = true;
            [ warn, params ] = this.handleOptionAndParams (params, 'cancelAllOrders', 'warnOnCancelAllOrdersWithOutcome', warn);
            if (warn) {
                throw new BadRequest (this.id + ' cancelAllOrders cancels all orders for entire slug (both YES and NO outcomes). Please provide params.slug to specify the slug, or set the warnOnCancelAllOrdersWithOutcome option to false to suppress this warning message.');
            }
        }
        const request: Dict = {};
        const slug = this.safeString (params, 'slug');
        if (outcome !== undefined) {
            const outcomeObj = await this.loadOutcome (outcome);
            request['slug'] = this.safeString (outcomeObj['info'], 'slug');
        } else if (slug === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelAllOrders requires either an outcome argument or a slug parameter');
        }
        const response = await this.limitlessPrivateDeleteOrdersAllSlug (this.extend (request, params));
        //
        //     {
        //         "message": "Orders canceled successfully"
        //     }
        //
        return [ this.safePredictionOrder ({ 'info': response }) ];
    }

    /**
     * @method
     * @name limitless#fetchMyTrades
     * @description fetch all trades made by the user
     * @see https://docs.limitless.exchange/api-reference/trades/get-trades
     * @param {string} [outcome] outcome, e.g. "TRUMP_OUT:YES"
     * @param {int} [since] the earliest time in ms to fetch trades for
     * @param {int} [limit] the maximum number of trades structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction trade structures](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    async fetchMyTrades (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionTrade[]> {
        // resolve the handle for the final filter — the caller may have passed an outcomeId
        let outcomeSymbol = outcome;
        if (outcome !== undefined) {
            const outcomeObj = await this.loadOutcome (outcome);
            outcomeSymbol = this.safeString (outcomeObj, 'outcome');
        }
        let paginate = false;
        const maxLimit = 100;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchMyTrades', 'paginate', paginate);
        if (paginate) {
            params = this.omit (params, 'paginate');
            return await this.fetchPaginatedCallCursor ('fetchMyTrades', outcome, since, limit, params, 'nextCursor', 'cursor', undefined, maxLimit);
        }
        const request: Dict = {};
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, maxLimit);
        }
        const response = await this.limitlessPrivateGetPortfolioHistory (this.extend (request, params));
        //
        //     {
        //         "data": [
        //             {
        //                 "action": "loss",
        //                 "blockTimestamp": 1778144400,
        //                 "collateralAmount": "2",
        //                 "collateralSymbol": "USDC",
        //                 "collateralToken": "7",
        //                 "conditionId": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //                 "market": {
        //                     "closed": true,
        //                     "collateral": {
        //                     "symbol": "USDC",
        //                     "id": "7",
        //                     "decimals": 6},
        //                     "group": null,
        //                     "condition_id": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //                     "conditionId": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //                     "funding": "0",
        //                     "id": "117515",
        //                     "slug": "doge-up-or-down-1-hour-1778140801775",
        //                     "title": "DOGE Up or Down - 1 hour",
        //                     "expirationDate": "2026-05-07T09:00:00.000Z"
        //                 },
        //                 "outcomeIndex": 1,
        //                 "pnl": "-2000000",
        //                 "title": "DOGE Up or Down - 1 hour"
        //             },
        //             {
        //                 "blockTimestamp": 1778144137,
        //                 "collateralAmount": "2",
        //                 "market": {
        //                     "closed": true,
        //                     "collateral": {
        //                         "symbol": "USDC",
        //                         "id": "7",
        //                         "decimals": 6
        //                     },
        //                     "group": null,
        //                     "condition_id": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //                     "conditionId": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //                     "funding": "0",
        //                     "id": "117515",
        //                     "slug": "doge-up-or-down-1-hour-1778140801775",
        //                     "title": "DOGE Up or Down - 1 hour",
        //                      "expirationDate": "2026-05-07T09:00:00.000Z"
        //                 },
        //                 "outcomeTokenAmount": "10",
        //                 "outcomeTokenAmounts": [
        //                     "10",
        //                     "0"
        //                 ],
        //                 "outcomeIndex": 0,
        //                 "outcomeTokenPrice": "0.2",
        //                 "strategy": "Limit Buy",
        //                 "transactionHash": "0x1e1167f09bb65ad3037610ae4f2521b696f7109f535e148ea388d42fdb6e2a10"
        //             }
        //         ],
        //         "nextCursor": null
        //     }
        //
        const data = this.safeList (response, 'data', []);
        // response contains both trade, settlement, split and merge history
        // we filter out the settlements here and only return the trades
        const trades: any[] = [];
        for (let i = 0; i < data.length; i++) {
            const item = this.safeDict (data, i);
            const strategy = this.safeStringLower (item, 'strategy');
            if (strategy !== undefined) {
                const buyIndex = strategy.indexOf ('buy');
                const sellIndex = strategy.indexOf ('sell');
                if ((buyIndex >= 0) || (sellIndex >= 0)) {
                    trades.push (item);
                }
            }
        }
        const parsedTrades = this.parsePredictionTrades (trades, undefined);
        return this.filterByOutcomeSinceLimit (parsedTrades, outcomeSymbol, since, limit);
    }

    /**
     * @ignore
     * @method
     * @name limitless#parsePredictionTrade
     * @description parses a raw trade from either the public market events feed or the private portfolio history into a unified trade object
     * @param {object} trade the raw trade object
     * @param {object} [market] the outcome object the trade belongs to
     * @returns {object} a [prediction trade structure](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    parsePredictionTrade (trade: Dict, market: Market = undefined): PredictionTrade {
        const matchedSize = this.safeString (trade, 'matchedSize');
        if (matchedSize !== undefined) {
            // public market events feed trade, see fetchTrades for the response sample
            const ts = this.parse8601 (this.safeString (trade, 'createdAt'));
            const sideRaw = this.safeString (trade, 'side');
            let feedSide = undefined;
            if (sideRaw === '0') {
                feedSide = 'buy';
            } else if (sideRaw === '1') {
                feedSide = 'sell';
            }
            const amountStr = Precise.stringDiv (matchedSize, '1000000');
            const priceStr = this.safeString (trade, 'price');
            let costStr = undefined;
            if (priceStr !== undefined) {
                costStr = Precise.stringMul (priceStr, amountStr);
            }
            const feedOutcome = this.safeOutcomeSymbol (undefined, market);
            return this.safePredictionTrade ({
                'id': this.safeString (trade, 'txHash'),
                'info': trade,
                'timestamp': ts,
                'datetime': this.iso8601 (ts),
                'outcome': feedOutcome,
                'outcomeId': this.safeString (market, 'outcomeId'),
                'label': this.safeString (market, 'label'),
                'market': this.safeString (market, 'market'),
                'order': undefined,
                'type': undefined,
                'side': feedSide,
                'takerOrMaker': 'taker',
                'price': this.parseNumber (priceStr),
                'amount': this.parseNumber (amountStr),
                'cost': this.parseNumber (costStr),
                'fee': undefined,
            });
        }
        //
        //     {
        //         "blockTimestamp": 1778144137,
        //         "collateralAmount": "2",
        //         "market": {
        //             "closed": true,
        //             "collateral": {
        //                 "symbol": "USDC",
        //                 "id": "7",
        //                 "decimals": 6
        //             },
        //             "group": null,
        //             "condition_id": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //             "conditionId": "0x0c61db7449dd8f8c81cd856f53d4186cf30888e27eb025d10c7908fa94ba736e",
        //             "funding": "0",
        //             "id": "117515",
        //             "slug": "doge-up-or-down-1-hour-1778140801775",
        //             "title": "DOGE Up or Down - 1 hour",
        //             "expirationDate": "2026-05-07T09:00:00.000Z"
        //         },
        //         "outcomeTokenAmount": "10",
        //         "outcomeTokenAmounts": [
        //             "10",
        //             "0"
        //         ],
        //         "outcomeIndex": 0,
        //         "outcomeTokenPrice": "0.2",
        //         "strategy": "Limit Buy",
        //         "transactionHash": "0x1e1167f09bb65ad3037610ae4f2521b696f7109f535e148ea388d42fdb6e2a10"
        //     }
        //
        const id = this.safeString (trade, 'transactionHash');
        const timestamp = this.safeIntegerProduct (trade, 'blockTimestamp', 1000);
        const price = this.safeString (trade, 'outcomeTokenPrice');
        const amount = this.safeString (trade, 'outcomeTokenAmount');
        const cost = this.safeString (trade, 'collateralAmount');
        const rawSide = this.safeStringLower (trade, 'strategy');
        const sellIndex = rawSide.indexOf ('sell');
        const side = (sellIndex >= 0) ? 'sell' : 'buy';
        let type = undefined;
        let takerOrMaker = undefined;
        if (rawSide.indexOf ('limit') >= 0) {
            type = 'limit';
            takerOrMaker = 'maker';
        } else if (rawSide.indexOf ('market') >= 0) {
            type = 'market';
            takerOrMaker = 'taker';
        }
        const rawMarket = this.safeDict (trade, 'market', {});
        const slug = this.safeString (rawMarket, 'slug');
        const outcomeIndex = this.safeInteger (trade, 'outcomeIndex');
        const label = (outcomeIndex === 0) ? 'yes' : 'no';
        const outcome = this.getOutcomeBySlugAndLabel (slug, label, market);
        const tradeOutcome = this.safeString (outcome, 'outcome');
        return this.safePredictionTrade ({
            'id': id,
            'info': trade,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'outcome': tradeOutcome,
            'outcomeId': this.safeString (trade, 'asset'),
            'label': this.safeString (outcome, 'label'),
            'market': this.safeString (outcome, 'market'),
            'order': undefined,
            'type': type,
            'side': side,
            'takerOrMaker': takerOrMaker, // todo check
            'price': price,
            'amount': amount,
            'cost': cost,
            'fee': undefined,
        });
    }

    getOutcomeBySlugAndLabel (slug: Str, label: Str, market: Market = undefined): any {
        const mkt = this.safeMarket (slug, market);
        const outcomes = this.safeList (mkt, 'outcomes', []);
        for (let i = 0; i < outcomes.length; i++) {
            const outcome = this.safeDict (outcomes, i);
            const outcomeLabel = this.safeString (outcome, 'label');
            if (outcomeLabel === label) {
                return outcome;
            }
        }
        return undefined;
    }

    /**
     * @method
     * @name limitless#fetchPositions
     * @description fetches open positions for the authenticated limitless user from the portfolio endpoint
     * @see https://docs.limitless.exchange/api-reference/portfolio/get-positions
     * @param {string[]} [outcomes] filter by outcome ids or outcomes
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction position structures](https://docs.ccxt.com/#/?id=prediction-position-structure)
     */
    async fetchPositions (outcomes: Strings = undefined, params = {}): Promise<PredictionPosition[]> {
        let symbolsLength = 0;
        if (outcomes !== undefined) {
            symbolsLength = outcomes.length;
        }
        if (symbolsLength > 0) {
            await this.loadOutcomes (outcomes);
        }
        // no bulk warm-up on the unfiltered path: the portfolio request is self-contained and
        // labels resolve cache-only (raw slugs/labels stay available in info when the cache is cold)
        const response = await this.limitlessPrivateGetPortfolioPositions (params);
        //
        //     {
        //         "rewards": {
        //                 "todaysRewards": "0",
        //                 "totalUnpaidRewards": "0",
        //                 "totalUserRewardsLastEpoch": "0",
        //                 "rewardsChartData": [],
        //                 "rewardsByEpoch": []
        //         },
        //         "points": "0.00000000",
        //         "accumulativePoints": "0.00000000",
        //         "amm": [],
        //         "group": [],
        //         "clob": [
        //             {
        //                 "market": {
        //                     "slug": "btc-above-dollar7982448-on-may-11-1000-utc-1777888806248",
        //                     "status": "FUNDED",
        //                     "title": "BTC Up or Down - 1 week",
        //                     "conditionId": "0xdcd8264cd09a6c50fca35eca24cda13e70f705e8b9ca7df7edb1c53d5e14ef91",
        //                     "id": 113280,
        //                     "address": null,
        //                     "closed": false,
        //                     "expirationDate": "2026-05-11T10:00:00.000Z",
        //                     "deadline": "2026-05-11T10:00:00.000Z",
        //                     "negRiskRequestId": null,
        //                     "winningOutcomeIndex": null,
        //                     "yesPositionId": "93872239373494820196551522390839917813776244436120184186867916673676200558660",
        //                     "noPositionId": "63415751165356883207530164011662686422066216695676262832702521399916548381548",
        //                     "collateralToken": {
        //                         "id": 7,
        //                         "decimals": 6,
        //                         "symbol": "USDC",
        //                         "address": "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
        //                     },
        //                     "venue": {
        //                         "exchange": "0x05c748E2f4DcDe0ec9Fa8DDc40DE6b867f923fa5",
        //                         "adapter": null,
        //                         "operator": null
        //                     },
        //                     "group": {}
        //                 },
        //                 "latestTrade": {
        //                     "outcomeTokenPrice": 0.991,
        //                     "latestNoPrice": 0.009,
        //                     "latestYesPrice": 0.991
        //                 },
        //                 "orders": {
        //                     "liveOrders": [],
        //                     "totalCollateralLocked": "0"
        //                 },
        //                 "positions": {
        //                     "no": {
        //                         "cost": "0",
        //                         "fillPrice": "0",
        //                         "marketValue": "0",
        //                         "realisedPnl": "0",
        //                         "unrealizedPnl": "0"
        //                     },
        //                     "yes": {
        //                         "cost": "995720",
        //                         "fillPrice": "991000",
        //                         "marketValue": "999919",
        //                         "realisedPnl": "0",
        //                         "unrealizedPnl": "0"
        //                     }
        //                 },
        //                 "tokensBalance": {
        //                     "no": "0",
        //                     "yes": "1004763"
        //                 },
        //                 "rewards": {
        //                     "isEarning": false,
        //                     "epochs": []
        //                 },
        //                 "makerAddress": "0xAb2B9833FC8B8f55F4De7C4A0FAb8577EF0F7b36"
        //             }
        //         ]
        //     }
        //
        const clob = this.safeList (response, 'clob', []) as any[];
        const result: PredictionPosition[] = [];
        const labels = [ 'yes', 'no' ];
        for (let i = 0; i < clob.length; i++) {
            const entry = this.safeDict (clob, i);
            for (let j = 0; j < labels.length; j++) {
                const label = this.safeString (labels, j);
                const position = this.getPositionFromClobEntry (label, entry);
                if (position !== undefined) {
                    result.push (position);
                }
            }
        }
        return result;
    }

    getPositionFromClobEntry (label: string, entry: Dict = undefined) {
        if (entry === undefined) {
            return undefined;
        }
        const tokensBalance = this.safeDict (entry, 'tokensBalance');
        const contracts = this.omitZero (this.safeString (tokensBalance, label));
        if (contracts === undefined) {
            return undefined;
        }
        const positions = this.safeDict (entry, 'positions');
        const position = this.safeDict (positions, label);
        const rawMarket = this.safeDict (entry, 'market');
        const slug = this.safeString (rawMarket, 'slug');
        const outcomeObj = this.getOutcomeBySlugAndLabel (slug, label);
        const parsed = this.parsePredictionPosition (position, outcomeObj);
        parsed['contracts'] = this.parseNumber (this.applyScale (contracts));
        const latestTrade = this.safeDict (entry, 'latestTrade');
        let key = 'latestYesPrice';
        if (label === 'no') {
            key = 'latestNoPrice';
        }
        parsed['markPrice'] = this.safeNumber (latestTrade, key);
        parsed['info'] = entry;
        return this.safePredictionPosition (parsed);
    }

    /**
     * @ignore
     * @method
     * @name limitless#parsePredictionPosition
     * @description parses a raw limitless portfolio position into a unified position object
     * @param {object} position the raw position object
     * @param {object} [market] the outcome object the position belongs to
     * @returns {object} a [prediction position structure](https://docs.ccxt.com/#/?id=prediction-position-structure)
     */
    parsePredictionPosition (position: Dict, market: Market = undefined): PredictionPosition {
        //
        //     {
        //         "cost": "995720",
        //         "fillPrice": "991000",
        //         "marketValue": "999919",
        //         "realisedPnl": "0",
        //         "unrealizedPnl": "0"
        //     }
        //
        const outcomeSymbol = this.safeString (market, 'outcome');
        const notional = this.applyScale (this.safeString (position, 'marketValue'));
        const unrealizedPnl = this.applyScale (this.safeString (position, 'unrealizedPnl'));
        const realizedPnl = this.applyScale (this.safeString (position, 'realisedPnl'));
        const collateral = this.applyScale (this.safeString (position, 'cost'));
        const entryPrice = this.applyScale (this.safeString (position, 'fillPrice'));
        return {
            'id': undefined,
            'outcome': outcomeSymbol,
            'outcomeId': this.safeString (market, 'outcomeId'),
            'label': this.safeString (market, 'label'),
            'market': this.safeString (market, 'market'),
            'timestamp': undefined,
            'datetime': undefined,
            'contracts': undefined,
            'contractSize': 1,
            'side': 'long',
            'notional': this.parseNumber (notional),
            'leverage': 1,
            'unrealizedPnl': this.parseNumber (unrealizedPnl),
            'realizedPnl': this.parseNumber (realizedPnl),
            'collateral': this.parseNumber (collateral),
            'entryPrice': this.parseNumber (entryPrice),
            'markPrice': undefined,
            'liquidationPrice': undefined,
            'hedged': false,
            'maintenanceMargin': undefined,
            'maintenanceMarginPercentage': undefined,
            'initialMargin': undefined,
            'initialMarginPercentage': undefined,
            'marginRatio': undefined,
            'marginMode': 'cross',
            'marginType': 'cross',
            'percentage': undefined,
            'info': position,
        } as unknown as PredictionPosition;
    }

    /**
     * @method
     * @name limitless#fetchEvents
     * @description fetches prediction-market events matching the given scope (query/queries/tags/eventId/slug — required) and caches their markets and outcomes on the instance
     * @see https://docs.limitless.exchange/api-reference/markets/search
     * @param {object} [params] extra exchange-specific parameters
     * @param {string} [params.query] a single search term; an eventId/slug does a direct lookup and tags resolve to limitless categories, paging only those categories' listings
     * @param {string[]} [params.queries] multiple search terms (alternative to query)
     * @param {string[]} [params.tags] category names to scope by (matched against GET /categories, e.g. ['crypto'])
     * @param {string} [params.eventId] direct lookup by market address or slug
     * @param {int} [params.limit] maximum number of markets per query, defaults to 50
     * @returns {object[]} an array of event structures
     */
    async fetchEvents (params: fetchEventsParams = {}): Promise<PredictionEvent[]> {
        this.requireEventQuery (params);
        const queries = this.parseSearchQueries (params);
        const queriesLength = queries.length;
        const rest = this.omit (params, [ 'query', 'queries', 'limit', 'sort', 'searchIn', 'eventId', 'slug', 'status' ]);
        const eventId = this.safeString2 (params, 'eventId', 'slug');
        // always fetch fresh from the API (never serve the possibly-cold cache): a query searches, an
        // eventId/slug does a direct lookup, and any other scope (tags) pages the active-markets listing
        const rawMarkets: any[] = [];
        if (queriesLength > 0) {
            const requestedLimit = this.safeInteger (params, 'limit', 50);
            // the search endpoint rejects limit > 50 - cap the per-query request
            const limit = Math.min (requestedLimit, 50);
            const seen: Dict = {};
            for (let i = 0; i < queries.length; i++) {
                const q = queries[i];
                const response = await this.limitlessPublicGetMarketsSearch (this.extend ({
                    'query': q,
                    'limit': limit,
                }, rest));
                const found = this.safeList (response, 'markets', []) as any[];
                for (let j = 0; j < found.length; j++) {
                    const raw = found[j];
                    const rawSlug = this.safeString (raw, 'slug');
                    if (rawSlug && !(rawSlug in seen)) {
                        seen[rawSlug] = true;
                        rawMarkets.push (raw);
                    }
                }
            }
        } else if (eventId !== undefined) {
            const response = await this.limitlessPublicGetMarketsAddressOrSlug (this.extend ({ 'addressOrSlug': eventId }, rest));
            rawMarkets.push (response);
        } else {
            // tags scope: resolve the tags to limitless categories and page only those
            // categories' listings server-side — never the whole active listing
            const requestedTags = this.safeList (params, 'tags', []);
            const listRaw = await this.fetchRawMarketsByTags (requestedTags, params);
            const listRawLength = listRaw.length;
            for (let i = 0; i < listRawLength; i++) {
                rawMarkets.push (listRaw[i]);
            }
        }
        if (!this.events) {
            this.events = {};
        }
        if (!this.markets) {
            this.markets = this.createSafeDictionary ();
        }
        const eventGroups: Dict = {};
        // group rows carry their tradeable children in a nested `markets` list — expand them
        // into regular rows before parsing (a group row itself has no tokens)
        const expandedMarkets = this.expandGroupRows (rawMarkets);
        const rawMarketsLength = expandedMarkets.length;
        for (let i = 0; i < rawMarketsLength; i++) {
            const raw = expandedMarkets[i];
            const groupId = this.safeStringN (raw, [ 'groupSlug', 'groupId' ], this.safeString (raw, 'slug'));
            const eventKey = groupId ? this.shortenSlug (groupId) : undefined;
            const m = this.parseMarket (raw);
            this.markets[m['market'] as string] = m;
            if (eventKey) {
                if (!(eventKey in eventGroups)) {
                    eventGroups[eventKey] = { 'groupId': groupId, 'title': this.safeString2 (raw, 'groupTitle', 'title', groupId), 'raw': raw, 'markets': [] };
                }
                const eventGroup = eventGroups[eventKey] as Dict;
                // push through a local and write the slice back — the go transpiler's
                // AppendToArray reassigns only a local copy of a map-stored array, so a
                // direct push on eventGroup['markets'] loses the element in go
                const groupMarkets = eventGroup['markets'];
                groupMarkets.push (m);
                eventGroup['markets'] = groupMarkets;
            }
        }
        const result: any[] = [];
        const eventKeys = Object.keys (eventGroups);
        const eventKeysLength = eventKeys.length;
        for (let i = 0; i < eventKeysLength; i++) {
            const g = eventGroups[eventKeys[i]] as Dict;
            const ev = this.parseEvent (g);
            result.push (ev);
        }
        // setEvents keys events by id/slug/handle; populateOutcomes rebuilds the outcome cache
        this.setEvents (result);
        this.populateOutcomes ();
        // the limitless search endpoint is FUZZY — it returns nearest-neighbour markets even
        // for queries that match nothing — so default searchIn to 'both' to post-filter the
        // results literally by title/description (an explicit params.searchIn still wins).
        // tags were already applied server-side (category-scoped listing); strip them before the
        // client-side pass — events built from raw markets carry venue tags, not category names,
        // so the base tag filter would wrongly drop server-matched events
        const searchParams = this.extend ({ 'searchIn': 'both' }, params);
        const postParams = this.omit (searchParams, [ 'tags' ]);
        return this.applyEventFetchParams (result, postParams, queries);
    }

    /**
     * @ignore
     * @method
     * @name limitless#fetchRawActiveMarkets
     * @description pages the active-markets listing (or a single category's listing), bounded by limit (or options.fetchMarketsLimit)
     * @param {object} [params] extra exchange-specific parameters
     * @param {int} [params.limit] max number of raw markets to collect
     * @param {string} [categoryId] a limitless category id — pages only that category's listing
     * @returns {object[]} raw limitless market objects
     */
    async fetchRawActiveMarkets (params = {}, categoryId: Str = undefined): Promise<any[]> {
        const maxMarkets = this.safeInteger (params, 'limit', this.safeInteger (this.options, 'fetchMarketsLimit', 1000));
        const pageSize = this.safeInteger (this.options, 'marketsPageSize', 25);
        const rest = this.omit (params, [ 'query', 'queries', 'limit', 'sort', 'searchIn', 'eventId', 'slug', 'status', 'tags' ]);
        const allRaw: any[] = [];
        let page = 1;
        let collected = 0;
        while (true) {
            const request: Dict = { 'page': page, 'limit': pageSize };
            let response = undefined;
            if (categoryId !== undefined) {
                request['categoryId'] = categoryId;
                response = await this.limitlessPublicGetMarketsActiveCategoryId (this.extend (request, rest));
            } else {
                response = await this.limitlessPublicGetMarketsActive (this.extend (request, rest));
            }
            const data = this.safeList (response, 'data', []) as any[];
            const dataLength = data.length;
            if (dataLength === 0) {
                break;
            }
            for (let i = 0; i < dataLength; i++) {
                if (collected < maxMarkets) {
                    allRaw.push (data[i]);
                    collected = this.sum (collected, 1);
                }
            }
            page = this.sum (page, 1);
            if (dataLength < pageSize || collected >= maxMarkets) {
                break;
            }
        }
        return allRaw;
    }

    /**
     * @ignore
     * @method
     * @name limitless#fetchRawMarketsByTags
     * @description resolves the requested tags to limitless categories via GET /categories, then pages only those categories' active listings server-side
     * @param {string[]} tags tag/category names to match (case-insensitive substring match on the category name)
     * @param {object} [params] extra exchange-specific parameters
     * @param {int} [params.limit] max number of raw markets to collect per category
     * @returns {object[]} raw limitless market objects, deduped by slug
     */
    async fetchRawMarketsByTags (tags: string[], params = {}): Promise<any[]> {
        const categoriesResponse = await this.limitlessPublicGetCategories ();
        const categories = (categoriesResponse !== undefined) ? categoriesResponse : [];
        const wanted = [];
        for (let i = 0; i < tags.length; i++) {
            wanted.push (tags[i].toLowerCase ());
        }
        const categoryIds = [];
        const categoriesLength = categories.length;
        for (let i = 0; i < categoriesLength; i++) {
            const category = categories[i];
            const name = this.safeStringLower (category, 'name', '');
            const categoryId = this.safeString (category, 'id');
            let matched = false;
            for (let wi = 0; wi < wanted.length; wi++) {
                if (name.indexOf (wanted[wi]) >= 0) {
                    matched = true;
                    break;
                }
            }
            if (matched && (categoryId !== undefined)) {
                categoryIds.push (categoryId);
            }
        }
        const categoryIdsLength = categoryIds.length;
        if (categoryIdsLength === 0) {
            throw new BadRequest (this.id + ' fetchEvents() could not match the requested tags to any limitless category — GET /categories lists the valid names');
        }
        const seen: Dict = {};
        const allRaw: any[] = [];
        for (let ci = 0; ci < categoryIdsLength; ci++) {
            const categoryMarkets = await this.fetchRawActiveMarkets (params, categoryIds[ci]);
            const categoryMarketsLength = categoryMarkets.length;
            for (let mi = 0; mi < categoryMarketsLength; mi++) {
                const raw = categoryMarkets[mi];
                const slug = this.safeString (raw, 'slug');
                if ((slug !== undefined) && !(slug in seen)) {
                    seen[slug] = true;
                    allRaw.push (raw);
                }
            }
        }
        return allRaw;
    }

    /**
     * @ignore
     * @method
     * @name limitless#sign
     * @description builds the request URL and attaches the lmts authentication headers for private endpoints
     * @param {string} path the endpoint path
     * @param {string|string[]} [section] the api group and access level
     * @param {string} [method] HTTP method
     * @param {object} [params] request parameters
     * @param {object} [headers] request headers
     * @param {object} [body] request body
     * @returns {object} a dictionary with url, method, body and headers
     */
    sign (path: any, section: any = 'limitless', method = 'GET', params = {}, headers: any = undefined, body: any = undefined) {
        const apiGroup: string = typeof section === 'string' ? section : section[0];
        const access: string = typeof section === 'string' ? 'public' : section[1];
        const baseUrls = this.urls['api'] as Dict;
        const baseUrl = this.safeString (baseUrls, apiGroup, baseUrls['limitless'] as string);
        let url = '/' + this.implodeParams (path, params);
        const query = this.omit (params, this.extractParams (path));
        const querystring = this.urlencodeWithArrayRepeat (query);
        if (method === 'GET' && querystring) {
            url += '?' + querystring;
        }
        if (access === 'private') {
            let bodyString = '';
            if (headers === undefined) {
                headers = {};
            }
            if (method === 'POST' && querystring) {
                bodyString = this.json (query);
                body = bodyString;
                const headerDefaults = (headers !== undefined) ? headers : {};
                headers = this.extend ({
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }, headerDefaults);
            }
            this.checkRequiredCredentials ();
            const timestamp = this.iso8601 (this.milliseconds ());
            const newline = "\n"; // eslint-disable-line quotes
            const payload = timestamp + newline + method + newline + url + newline + bodyString;
            const signature = this.hmac (this.encode (payload), this.base64ToBinary (this.secret), sha256, 'base64');
            headers = this.extend (headers, {
                'lmts-api-key': this.apiKey,
                'lmts-timestamp': timestamp,
                'lmts-signature': signature,
            });
        }
        url = baseUrl + url;
        return { 'url': url, 'method': method, 'body': body, 'headers': headers };
    }

    /**
     * @ignore
     * @method
     * @name limitless#handleErrors
     * @description maps limitless error responses to ccxt exceptions
     */
    handleErrors (statusCode: int, statusText: string, url: string, method: string, responseHeaders: Dict, responseBody: string, response, requestHeaders, requestBody) {
        if (response === undefined) {
            return undefined;
        }
        if ((statusCode >= 200) && (statusCode < 300)) {
            return undefined;
        }
        const feedback = this.id + ' ' + responseBody;
        // the API returns either a string message or an array of field-validation errors
        const message = this.safeString (response, 'message');
        if (message !== undefined) {
            this.throwExactlyMatchedException (this.exceptions['exact'], message, feedback);
        }
        this.throwBroadlyMatchedException (this.exceptions['broad'], responseBody, feedback);
        // a 400 is a client-side bad request (bad params, or a business rule like "market not
        // resolved"), not a transport outage — throw BadRequest with the exchange message instead
        // of letting the base map the bare 400 to a retryable network-unavailable error
        if (statusCode === 400) {
            throw new BadRequest (feedback);
        }
        return undefined;
    }
}
